import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlayIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  ShieldCheckIcon,
  ClockIcon,
  KeyIcon,
  ShieldExclamationIcon,
  EyeSlashIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  CpuChipIcon,
  LinkIcon,
  InboxStackIcon,
  InformationCircleIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { getNextIPStatus, getNextUrlStatus } from '../utils/riskColors';
import { aliasIdentifiers, identityBannerState } from '../utils/identityResolution';
import { OUTLOOK_NOISE_OPS, parseItemAttachments } from '../utils/eventUtils';
import { openClientReport, openTechnicalReport } from '../utils/storyGenerator';
import { analyzeRecoveredLegs, caseIdentityIds, isVisibleStandaloneLeg } from '../utils/signInTransactions';
import { buildMeetingDeviceBaseline } from '../utils/teamsMeetings';
import { buildAttackerContextFrom, parseAttackerEventMarks, safeMarkedIds } from '../utils/attackerAttribution';
import { parseSessionClassifications, sessionStatusOf, attackerSessionMarks, markedSessionStarts } from '../utils/sessionMarks';
import { SessionMarkContext } from './SessionMarkContext';
import {
  computeNoAccessEligibility, isNoAccessScope,
  SCOPE_NO_ACCESS, SCOPE_ACCESS_CONFIRMED,
} from '../utils/compromiseScope';
import {
  calculateAttackerWindow,
  isPostWindowEvent,
  isPreWindowEvent,
  analystEntryPointIds,
  isPreCompromiseSessionEvent,
  isPostCompromiseSessionEvent,
  shouldOverrideHideForSessionCompromise,
  deriveSafeIpSessionSet,
} from '../utils/attackerWindow';
import {
  flaggedPhishMessageIndex,
  collectReleaseEvidence,
  stampFlaggedReleases,
  stampAndReinjectFlaggedReleases,
} from '../utils/quarantineRelease';
import { format, parseISO } from 'date-fns';
import {
  fetchCase,
  updateCase,
  deleteCase,
  triggerRunbook,
  pollRunbookStatus,
  fetchCaseResults,
  fetchServiceIPs,
  fetchTenantSafeIPs,
  fetchKnownApps,
  addKnownApp,
  removeKnownApp,
  fetchTenants,
  logSubjectView,
  refreshIpEnrichment,
  refreshIpEnrichmentBatch,
  fetchCaseIpEvidence,
  fetchCaseNonInteractiveIps,
  fetchCaseUserAlerts,
  addKnownMaliciousIPsFromCase,
} from '../api/client';
import TimelineView, { TIMEZONE_OPTIONS, TZ_STORAGE_KEY, getDefaultTimezone, getTzOffset, resolveIana, getTzLabel } from './TimelineView';
import { caseAlertWindow, caseAlertIdentifiers, toCaseAlertEvents } from '../utils/caseAlerts';
import ExecutiveReportModal from './ExecutiveReportModal';
import { isDemoMode, DEMO_UNAVAILABLE_TITLE } from '../utils/demoMode';
import IPAddressTable from './IPAddressTable';
import { buildSessionIndex, makeActivityInWindow } from '../utils/caseSessionIndex';
import { buildNonInteractivePersist } from '../utils/nonInteractivePersist';
import SummaryView from './SummaryView';
import RawLogViewer from './RawLogViewer';
import ServiceIPEventsTab from './ServiceIPEventsTab';
import SafeIPEventsTab from './SafeIPEventsTab';
import DevicesTab from './DevicesTab';
import SessionsTab from './SessionsTab';
import BaselineTab from './BaselineTab';
import Badge from './ui/Badge';
import FilterPill from './ui/FilterPill';
import { canonicalFilterState, filterProfileDiff } from '../utils/execReportFilterProfile';
import { caseStatusTone } from './ui/badgeTones';
import CopyButton from './CopyButton';
import CaseScopeNotice from './CaseScopeNotice';

/**
 * CaseDetail - Main investigation page for a single case.
 *
 * Handles:
 *   - Displaying case metadata (header)
 *   - Date range controls and "Retrieve Logs" button
 *   - Polling runbook execution status
 *   - Tabbed results view (Timeline, IP Addresses, Summary, Raw Logs)
 *   - Known-good IP management
 */

const TABS = [
  { id: 'timeline', label: 'Timeline' },
  { id: 'ips', label: 'IP Addresses' },
  { id: 'devices', label: 'Devices' },
  { id: 'sessions', label: 'AAD Session IDs' },
  { id: 'baseline', label: 'Baseline' },
  { id: 'summary', label: 'Summary' },
  { id: 'raw', label: 'Raw Logs' },
  { id: 'service-ips', label: 'Service IPs' },
  { id: 'safe-ips', label: 'Safe IP Events' },
];

const POLL_INTERVAL_MS = 3000;
// A poll that errors is not evidence the investigation failed — the run lives in
// Azure Automation, not in the portal. Ride out a short run of transient failures
// (~15 s at the interval above) before telling the analyst anything.
const POLL_MAX_CONSECUTIVE_ERRORS = 5;

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

/**
 * Recalculates summary statistics from filtered sign-in events.
 */
function recalculateSummary(filteredSignInEvents) {
  const events = filteredSignInEvents;
  const byApp = {};
  const byOS = {};
  const byCountry = {};
  const byCity = {};
  const byHour = {};
  const byStatus = {};
  let highRisk = 0;

  events.forEach((evt) => {
    const app = evt.ApplicationName || evt.AppDisplayName || 'Unknown';
    byApp[app] = (byApp[app] || 0) + 1;

    const os = evt.DeviceOS || 'Unknown';
    byOS[os] = (byOS[os] || 0) + 1;

    const country = evt.Location?.Country || evt.IP_Country || 'Unknown';
    byCountry[country] = (byCountry[country] || 0) + 1;

    const city = evt.Location?.City || evt.IP_City || 'Unknown';
    byCity[city] = (byCity[city] || 0) + 1;

    if (evt.EventTimeUTC) {
      try {
        const hour = new Date(evt.EventTimeUTC).getUTCHours();
        byHour[String(hour)] = (byHour[String(hour)] || 0) + 1;
      } catch { /* ignore */ }
    }

    const status = evt.ResultStatus || evt.Status || 'Unknown';
    byStatus[status] = (byStatus[status] || 0) + 1;

    const risk = evt.RiskScore ?? evt.Risk_Total ?? 0;
    if (risk >= 100) highRisk++;
  });

  return {
    TotalEvents: events.length,
    HighRiskCount: highRisk,
    SuccessCount: byStatus.Success || 0,
    FailureCount: byStatus.Failure || byStatus.Failed || 0,
    ByApplication: byApp,
    ByOS: byOS,
    ByCountry: byCountry,
    ByCity: byCity,
    ByHour: byHour,
    ByStatus: byStatus,
  };
}

// Overlay a fresh IP-enrichment payload (alert-row field names, as returned by
// /api/manage/ip-enrichment/refresh[-batch]) onto an IPAddresses table row.
// VPN/Proxy/TOR are derived from the comma-joined `ipDetections` string.
function applyEnrichmentToRow(row, fresh) {
  const detSet = new Set(
    String(fresh.ipDetections || '')
      .toLowerCase()
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  );
  return {
    ...row,
    Country: fresh.ipCountry ?? row.Country,
    City: fresh.city ?? row.City,
    State: fresh.regionName ?? row.State,
    Provider: fresh.ipProvider ?? row.Provider,
    RiskLevel: fresh.ipRiskLevel ?? row.RiskLevel,
    NetworkType: fresh.networkType ?? row.NetworkType,
    IsCompromised: !!fresh.isCompromised,
    IsAnonymous: !!fresh.isAnonymous,
    IsVPN: detSet.has('vpn'),
    IsProxy: detSet.has('proxy'),
    IsTOR: detSet.has('tor'),
  };
}

export default function CaseDetail({ isAdmin = false }) {
  const { caseId } = useParams();
  const navigate = useNavigate();

  // Case data
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Delete case state
  const [deleting, setDeleting] = useState(false);

  // Results data
  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState(null);

  // Date range overrides
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Runbook execution state
  const [runbookRunning, setRunbookRunning] = useState(false);
  const [runbookStatus, setRunbookStatus] = useState(null);
  const [runbookError, setRunbookError] = useState(null);
  // True when the outcome is UNKNOWN rather than bad: a status poll we could not
  // read, or a trigger whose response was lost. Both need different words from a
  // failure, because the run lives in Azure Automation — losing sight of it says
  // nothing about whether it succeeded. See startPolling.
  const [runbookOutcomeUnknown, setRunbookOutcomeUnknown] = useState(false);
  const pollTimerRef = useRef(null);
  // Incremented on every startPolling AND on every teardown, so a response that
  // arrives from a superseded poll cycle can be discarded instead of writing state.
  const pollGenRef = useRef(0);
  // setInterval fires on a schedule, not on completion — without this a slow poll
  // lets ticks stack up behind it.
  const pollInFlightRef = useRef(false);
  const pollFailStreakRef = useRef(0);

  // Timezone selection
  const [selectedTz, setSelectedTz] = useState(getDefaultTimezone);
  // Which document type's modal is open, or null. NOT a boolean plus a separate type
  // variable -- two variables for one fact can disagree, and the one they would disagree
  // about is which document a provider call produces.
  const [execReportType, setExecReportType] = useState(null);
  const handleTzChange = useCallback((e) => {
    const tz = e.target.value;
    setSelectedTz(tz);
    try { localStorage.setItem(TZ_STORAGE_KEY, tz); } catch { /* ignore */ }
  }, []);

  // IP classifications: { "1.2.3.4": "safe", "5.6.7.8": "malicious" }
  const [ipClassifications, setIPClassifications] = useState({});
  // Session classifications: { "<aadSessionId>": "safe" } (v1.53.2+)
  // 2-state in v1 (default ↔ safe); compromised takes precedence at filter time.
  const [sessionClassifications, setSessionClassifications] = useState({});
  const sessionClassSaveTimerRef = useRef(null);
  const sessionClassificationsRef = useRef({});
  const [hideSafeSessions, setHideSafeSessions] = useState(true);
  // "Extend safe to sessions" (v1.89.0) — hide NON-sign-in events whose session was
  // first seen from an analyst-Safe IP (and never touched a malicious IP). Label-first:
  // defaults OFF so the analyst sees the count and opts in. Live-derived, ephemeral.
  const [hideSafeIpSessions, setHideSafeIpSessions] = useState(false);
  // Tenant-level safe IPs
  const [tenantSafeIPs, setTenantSafeIPs] = useState([]);
  // Debounced save for IP classification changes
  const ipClassSaveTimerRef = useRef(null);
  const ipClassificationsRef = useRef({});
  const [ipClassSaving, setIPClassSaving] = useState(false);
  // IP comments: { "1.2.3.4": "Atlanta Office IP" } — freeform per-case IP labels.
  // Edited only on the IP Addresses tab; read-only on Timeline / Devices / reports.
  const [ipComments, setIPComments] = useState({});
  const ipCommentSaveTimerRef = useRef(null);
  const ipCommentsRef = useRef({});
  // Persisted overlays (v1.98.2): enrichment refreshes + real-user evidence survive a
  // reload by being stored on the case entity (the investigation blob isn't rewritten).
  const enrichmentOverlayRef = useRef({});   // { ipLower: fresh enrichment payload }
  // Same map as state (v1.147.1). The ref alone was not enough: hydration applied the
  // overlay to `results.IPAddresses` ONCE, but the filter memo synthesises extra "stub"
  // rows for IPs seen in events yet absent from the results blob — and those rows are in
  // the table, so "Enrich unenriched" enriches them. On reload the stub was rebuilt blank
  // and hydration never reached it (it is not in results.IPAddresses), so exactly the IPs
  // the batch button targets lost their enrichment on every refresh. Applying the overlay
  // where the rows are ASSEMBLED fixes that for stubs and blob rows alike.
  const [enrichmentOverlay, setEnrichmentOverlay] = useState({});
  const evidenceRef = useRef({});            // { ipLower: evidence record }
  const overlaySaveTimerRef = useRef(null);
  const overlayHydratedRef = useRef(false);
  const [persistedEvidence, setPersistedEvidence] = useState(null);
  // Non-interactive sign-in aggregate (v1.147.0). The `graph_signin_noninteractive`
  // feed has its own Delta table and the investigation runbook reads only the unified
  // events table, so every case is blind to token refresh / SSO reuse / PRT replay
  // until this is fetched on demand. Shape: the endpoint's response envelope
  // { window, coverage, failureCoverage, ips[], devices[], summary }.
  const [nonInteractive, setNonInteractive] = useState(null);
  const nonInteractiveRef = useRef(null);
  const nonInteractiveSaveTimerRef = useRef(null);
  // Portal detection alerts about this user inside the case window, as All Events rows
  // (utils/caseAlerts.js). Fetched at view time from DetectionAlerts, so they need no
  // re-run and carry the alert's current status. `caseAlertsError` keeps a failed fetch
  // from reading as "no alerts".
  const [caseAlertRows, setCaseAlertRows] = useState([]);
  const [caseAlertsError, setCaseAlertsError] = useState('');
  // Hide safe IP events toggle (default: hidden)
  const [hideSafeIPs, setHideSafeIPs] = useState(true);
  const [hideKnownApps, setHideKnownApps] = useState(true);
  // MS-substrate background-read suppression. Shipped label-first in v1.88.0 with the
  // default OFF, gated on the A1 proof gate (Design 2026-06-15 §13).
  //
  // A1 PROOF GATE ACCEPTED — Dale, 2026-08-11 (v1.141.0), default now ON.
  // A1 = "AppAccessContext.APIId == ClientAppId is not forgeable by a non-Microsoft
  // actor for a mailbox read." Basis accepted: the design's §3.4 FLEET EVIDENCE — 0 of
  // 1.365M self-token + UserType=5 Exchange events came from an external or consented
  // app. Not the Microsoft-documentation basis and not a controlled token test; those
  // were never obtained.
  //
  // Be straight about what is missing: the design pairs default-on with ONGOING
  // MONITORING, and no monitoring was ever built — there is no recurring query, alert or
  // dashboard watching for a self-token read from a non-Microsoft app. That is tracked
  // as a follow-up, not silently satisfied by this flip.
  //
  // Why this default can move while post-window / failed / unresolved-mail cannot:
  // substrate reads are never evidence at ANY stage of an investigation — sessionless,
  // self-issued-token background reads with no user or attacker behind them — so hiding
  // them early costs nothing. The guardrails still hold (any missing field => Unknown =>
  // shown), a threat-flagged event stays visible via mustShowDespiteImplicitTrust, and
  // the pill is always rendered so the analyst can see the state and switch it off.
  const [hideMsSubstrate, setHideMsSubstrate] = useState(true);
  // Hide noisy Outlook background ops (OutlookItemPrioritized, etc.) — sessionless
  // mailbox telemetry with no user/attacker signal. Defaults OFF (shown); the
  // analyst opts in, mirroring the MS-substrate toggle. Button only renders when
  // the case actually contains such events (see hasOutlookNoise).
  const [hideOutlookNoise, setHideOutlookNoise] = useState(false);
  // Service IPs are always filtered via the Safe IPs path (service IPs are in effectiveSafeIPSet).
  // The dedicated Service IPs toggle was removed — it was redundant and confusing.
  // Attacker window: hide post-window events toggle (default: OFF — opt-in)
  const [hidePostWindow, setHidePostWindow] = useState(false);
  // Risk Detections in All Events. OFF by default: present during first review,
  // hidden once read. Scopes All Events only -- the sub-tab always shows every row.
  const [hideRiskDetections, setHideRiskDetections] = useState(false);
  // Email subjects (potential PHI/PII) are masked by default across the whole case;
  // one per-case reveal unmasks them everywhere and logs a single audit entry.
  const [revealSubjects, setRevealSubjects] = useState(false);
  // Hide failed events toggle (default: OFF — show failed events)
  const [hideFailedEvents, setHideFailedEvents] = useState(false);
  // Hide DLP events toggle (default: ON — DLP policy matches are noise for compromise investigations)
  const [hideDlpEvents, setHideDlpEvents] = useState(true);
  // Hide unresolved-service-IP MailItemsAccessed events toggle (default: OFF — opt-in).
  // These are MailItemsAccessed (Operation) events whose IP could not be resolved past the
  // Microsoft service proxy (IPResolutionMethod='Unresolved-ServiceIP') — high-volume noise
  // that buries the easier-to-diagnose events. Label-first: defaults OFF so the analyst sees
  // the count and opts in.
  const [hideUnresolvedMailAccess, setHideUnresolvedMailAccess] = useState(false);
  // Events STRICTLY BEFORE the attacker window opened, from IPs not classified malicious.
  //
  // 🔴 DEFAULT OFF, and that is the whole design. Activity before the established window
  // is a LEAD, not noise -- an earlier foothold, a second account, a staged phish -- and
  // the compromise point is a seed rather than proof. So it stays visible while the window
  // is being established, and engaging the pill at the end is the analyst stating they
  // looked and it is not part of the story. Required ON by the report's canonical view.
  const [hidePreWindow, setHidePreWindow] = useState(false);
  // Hide email-only IPs toggle (default: ON — email gateway IPs are noise)
  const [hideEmailIPs, setHideEmailIPs] = useState(true);
  // Hide lookback correlation sign-ins toggle (default: ON — lookback events are context, not timeline)
  const [hideLookback, setHideLookback] = useState(true);
  // Hide pre-compromise session activity toggle (default: ON — once a malicious IP is
  // classified, sessions it touched have a clear compromise time; events under those
  // sessions BEFORE that time are legitimate prior session use, not attacker activity).
  // While no IPs are classified Malicious yet, compromisedSessions is empty and this
  // toggle is a no-op — it only takes effect once the analyst identifies the compromise.
  const [hidePreCompromise, setHidePreCompromise] = useState(true);
  // Dismissed event IDs: Set of EventId strings dismissed by the investigator
  const [dismissedEventIds, setDismissedEventIds] = useState(new Set());
  const dismissedIdsRef = useRef(new Set());
  const dismissedSaveTimerRef = useRef(null);
  // Rows the analyst marked as ATTACKER ACTIVITY (v1.189.0): Map<eventId, { reason, by, at }>.
  // For a row whose own recorded address is a Microsoft service's — an authenticator
  // registered through a stolen session carries My Signins' backend IP — so classifying the
  // IP can never attribute it. Saved IMMEDIATELY (not debounced): it is a deliberate act with
  // a written reason, the server stamps who and when, and the report reads it off the entity.
  const [attackerEventMarks, setAttackerEventMarks] = useState(() => new Map());
  const attackerMarksRef = useRef(new Map());
  // Dismissed events display mode: 'hidden' | 'shown' | 'only'
  const [dismissedMode, setDismissedMode] = useState('hidden');
  // URL click classifications: { "eventId": "confirmed" | "suspected" | "benign" }
  const [urlClassifications, setUrlClassifications] = useState({});
  const urlClassificationsRef = useRef({});
  const urlClassSaveTimerRef = useRef(null);
  // Phishing flags: array of { eventId, senderAddress, subject }
  const [phishingFlags, setPhishingFlags] = useState([]);
  const phishingFlagsRef = useRef([]);
  const phishingFlagsSaveTimerRef = useRef(null);
  // Per-tenant token lifetime (default 90 min)
  const [tenantTokenLifetime, setTenantTokenLifetime] = useState(90);
  // Include per-log-type breakdown in story reports
  const [includeLogBreakdown, setIncludeLogBreakdown] = useState(false);

  // Known enterprise apps (tenant-scoped + global)
  const [knownApps, setKnownApps] = useState([]);
  const [knownAppSaving, setKnownAppSaving] = useState(false);

  // Service IPs (for filtering)
  const [serviceIPs, setServiceIPs] = useState([]);
  const [serviceIPObjects, setServiceIPObjects] = useState([]);

  // Active tab
  const [activeTab, setActiveTab] = useState('timeline');

  // Status toggle (close/reopen)
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState(null);

  // Case comment — post-investigation analyst remark, shown in the case list.
  const [comment, setComment] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  // Unified Audit Log attestation (v1.170.0) — GLOBAL ADMIN ONLY.
  //
  // 🔴 An operator's statement that records for this case were retrieved directly from
  // Microsoft's Unified Audit Log, outside the monitored feeds. It changes what a
  // client-facing negative finding is allowed to say: `Get-TenantWorkloadCoverage` can only
  // see what reached the warehouse, so an out-of-band retrieval reads as a feed that started
  // late and the Brief hedges a finding the analyst actually established.
  //
  // Sent ONLY from this handler — never folded into the generic autosave — because the API
  // rejects the field from a non-global-admin. A blanket send would 403 an unrelated save.
  const [ualReviewed, setUalReviewed] = useState(false);
  const [ualSaving, setUalSaving] = useState(false);

  // Compromise scope (v1.182.0) — GLOBAL ADMIN ONLY. On an attacker-in-the-middle case the
  // credentials/token were CAPTURED (a successful phishing-kit sign-in on the victim's own
  // IP) but no attacker ACCESS was found. This attestation softens the report wording from
  // "the attacker had access" to "a window of exposure, but no observed access". Gated on the
  // preconditions in computeNoAccessEligibility. Sent from its OWN handler (the API 403s a
  // non-admin), never the generic autosave.
  const [compromiseScope, setCompromiseScope] = useState(SCOPE_ACCESS_CONFIRMED);
  const [scopeSaving, setScopeSaving] = useState(false);

  const handleSetCompromiseScope = useCallback(async (next) => {
    setScopeSaving(true);
    const prev = compromiseScope;
    setCompromiseScope(next);           // optimistic
    try {
      await updateCase(caseId, { compromiseScope: next });
    } catch (err) {
      setCompromiseScope(prev);         // never keep a state the server refused
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Could not record the compromise scope.');
    } finally {
      setScopeSaving(false);
    }
  }, [caseId, compromiseScope]);

  const handleToggleUalReviewed = useCallback(async (next) => {
    setUalSaving(true);
    const prev = ualReviewed;
    setUalReviewed(next);           // optimistic: the checkbox must not lag the click
    try {
      await updateCase(caseId, { ualReviewed: next });
    } catch (err) {
      setUalReviewed(prev);         // and must not keep a state the server refused
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Could not record the Unified Audit Log attestation.');
    } finally {
      setUalSaving(false);
    }
  }, [caseId, ualReviewed]);

  const handleSaveComment = useCallback(async () => {
    const next = (commentDraft || '').trim();
    if (next === (comment || '')) return;
    setCommentSaving(true);
    try {
      await updateCase(caseId, { Comment: next });
      setComment(next);
      setCommentDraft(next);
    } catch {
      setCommentDraft(comment || '');
    } finally {
      setCommentSaving(false);
    }
  }, [caseId, comment, commentDraft]);

  // Load case data
  const loadCase = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCase(caseId);
      setCaseData(data);
      // Initialize date range from case data (convert ISO to YYYY-MM-DD for date inputs)
      if (data.startDate) setStartDate(data.startDate.substring(0, 10));
      if (data.endDate) setEndDate(data.endDate.substring(0, 10));
      setComment(data.comment || '');
      setUalReviewed(Boolean(data.ualReviewed));
      setCompromiseScope(isNoAccessScope(data.compromiseScope) ? SCOPE_NO_ACCESS : SCOPE_ACCESS_CONFIRMED);
      setCommentDraft(data.comment || '');
      // Load IP classifications (new format) with backward compat from knownGoodIPs
      let classifications = {};
      if (data.ipClassifications) {
        const raw = typeof data.ipClassifications === 'string'
          ? JSON.parse(data.ipClassifications || '{}')
          : data.ipClassifications;
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) classifications = raw;
      }
      // Backward compat: migrate knownGoodIPs to ipClassifications if empty
      if (Object.keys(classifications).length === 0 && data.knownGoodIPs) {
        const ips = typeof data.knownGoodIPs === 'string'
          ? JSON.parse(data.knownGoodIPs || '[]')
          : data.knownGoodIPs;
        if (Array.isArray(ips)) {
          ips.forEach(ip => { classifications[ip] = 'safe'; });
        }
      }
      setIPClassifications(classifications);
      ipClassificationsRef.current = classifications;
      // Load session classifications — { sessionId -> 'safe' } map (v1.53.2+).
      // Mirrors ipClassifications. Only `safe` is persisted in v1.
      // v1.190.0: a value is 'safe' or { verdict: 'attacker', reason, by, at } (utils/sessionMarks.js).
      const sessClassifications = parseSessionClassifications(data.sessionClassifications);
      setSessionClassifications(sessClassifications);
      sessionClassificationsRef.current = sessClassifications;
      // Load dismissed event IDs
      let dismissed = new Set();
      if (data.dismissedEventIds) {
        try {
          const raw = typeof data.dismissedEventIds === 'string'
            ? JSON.parse(data.dismissedEventIds || '[]')
            : data.dismissedEventIds;
          if (Array.isArray(raw)) dismissed = new Set(raw);
        } catch { /* ignore parse errors */ }
      }
      setDismissedEventIds(dismissed);
      dismissedIdsRef.current = dismissed;
      // Load the analyst's attacker-activity marks (v1.189.0)
      const marks = parseAttackerEventMarks(data.attackerEventIds);
      setAttackerEventMarks(marks);
      attackerMarksRef.current = marks;
      // Load URL click classifications
      let urlClass = {};
      if (data.urlClassifications) {
        try {
          const raw = typeof data.urlClassifications === 'string'
            ? JSON.parse(data.urlClassifications || '{}')
            : data.urlClassifications;
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) urlClass = raw;
        } catch { /* ignore parse errors */ }
      }
      setUrlClassifications(urlClass);
      urlClassificationsRef.current = urlClass;
      // Load phishing flags
      let pFlags = [];
      if (data.phishingFlags) {
        try {
          const raw = typeof data.phishingFlags === 'string'
            ? JSON.parse(data.phishingFlags || '[]')
            : data.phishingFlags;
          if (Array.isArray(raw)) pFlags = raw;
        } catch { /* ignore parse errors */ }
      }
      setPhishingFlags(pFlags);
      phishingFlagsRef.current = pFlags;
      // Load IP comments — { "<ip>": "Atlanta Office IP" } map. Mirrors ipClassifications.
      let comments = {};
      if (data.ipComments) {
        try {
          const raw = typeof data.ipComments === 'string'
            ? JSON.parse(data.ipComments || '{}')
            : data.ipComments;
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) comments = raw;
        } catch { /* ignore parse errors */ }
      }
      setIPComments(comments);
      ipCommentsRef.current = comments;
    } catch (err) {
      setError(err.message || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  // Load results
  const loadResults = useCallback(async () => {
    setResultsLoading(true);
    setResultsError(null);
    try {
      const data = await fetchCaseResults(caseId);
      setResults(data);
    } catch (err) {
      // 404 means no results yet -- not an error
      if (err.message && err.message.includes('404')) {
        setResults(null);
      } else {
        setResultsError(err.message || 'Failed to load results');
      }
    } finally {
      setResultsLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadCase().then(() => {
      // Load tenant safe IPs after case loads (need tenantId)
    });
    loadResults();
    fetchServiceIPs()
      .then((ips) => {
        setServiceIPObjects(ips);
        setServiceIPs(ips.map((ip) => ip.ipAddress || ip.IPAddress));
      })
      .catch(() => {});
  }, [loadCase, loadResults]);

  // Load tenant safe IPs and known apps when caseData.tenantId becomes available
  useEffect(() => {
    if (caseData?.tenantId) {
      fetchTenantSafeIPs(caseData.tenantId)
        .then((ips) => setTenantSafeIPs(Array.isArray(ips) ? ips.map((ip) => ip.ipAddress) : []))
        .catch(() => {});
      fetchKnownApps(caseData.tenantId)
        .then((apps) => setKnownApps(Array.isArray(apps) ? apps : []))
        .catch(() => {});
      fetchTenants()
        .then((tenants) => {
          const t = tenants.find((tn) => tn.TenantID === caseData.tenantId);
          if (t?.TokenLifetimeMinutes) setTenantTokenLifetime(parseInt(t.TokenLifetimeMinutes) || 90);
        })
        .catch(() => {});
    }
  }, [caseData?.tenantId]);

  // Filtered results based on service IPs and safe IPs
  const serviceIPSet = useMemo(() => new Set(serviceIPs), [serviceIPs]);

  // Service IP label lookup: IP → label (for fallback in ISP/Provider column)
  const serviceIPLabelMap = useMemo(() => {
    const map = {};
    serviceIPObjects.forEach(ip => {
      const addr = ip.ipAddress || ip.IPAddress;
      const label = ip.label || ip.Label || '';
      if (addr && label) map[addr] = label;
    });
    return map;
  }, [serviceIPObjects]);

  // Compute effective safe IPs from ipClassifications + tenant safe IPs + service IPs + known enterprise app IPs
  const effectiveSafeIPs = useMemo(() => {
    const effective = [];
    const seen = new Set();
    // Tenant safe IPs default to safe unless explicitly overridden in classifications
    tenantSafeIPs.forEach(ip => {
      const status = ipClassifications[ip];
      if (!status || status === 'safe') { effective.push(ip); seen.add(ip); }
    });
    // Case-level IPs classified as safe
    Object.entries(ipClassifications).forEach(([ip, status]) => {
      if (status === 'safe' && !seen.has(ip)) { effective.push(ip); seen.add(ip); }
    });
    // Service IPs are inherently safe infrastructure — treat as safe unless explicitly overridden
    serviceIPs.forEach(ip => {
      if (!seen.has(ip)) {
        const status = ipClassifications[ip];
        if (!status || status === 'default' || status === 'safe') {
          effective.push(ip);
          seen.add(ip);
        }
      }
    });
    // Known enterprise app IPs — app infrastructure IPs (e.g., Druva backup servers)
    // are treated as safe unless explicitly overridden to suspect/malicious.
    // Use live knownApps state (not stale app.isKnown from runbook snapshot).
    const knownAppIds = new Set(knownApps.map(ka => ka.appId));
    const apps = results?.enterpriseApps || [];
    apps.forEach(app => {
      if (knownAppIds.has(app.appId) && Array.isArray(app.ipsUsed)) {
        app.ipsUsed.forEach(ip => {
          if (!seen.has(ip)) {
            const status = ipClassifications[ip];
            if (!status || status === 'default' || status === 'safe') {
              effective.push(ip);
              seen.add(ip);
            }
          }
        });
      }
    });
    return effective;
  }, [tenantSafeIPs, ipClassifications, results, knownApps, serviceIPs]);

  const effectiveSafeIPSet = useMemo(() => new Set(effectiveSafeIPs), [effectiveSafeIPs]);

  // Email-only IPs: IPs that appear only in message trace events (email gateways, filtering providers)
  const emailOnlyIPSet = useMemo(() => {
    if (!results) return new Set();

    // Use runbook-provided IsEmailOnlyIP flag if available
    const flaggedSet = new Set();
    (results.IPAddresses || []).forEach(ip => {
      if (ip.IsEmailOnlyIP) flaggedSet.add(ip.IPAddress);
    });
    if (flaggedSet.size > 0) return flaggedSet;

    // Fallback: compute client-side for pre-change runbook blobs
    const nonMsgTraceIPs = new Set();
    [
      results.SignInEvents, results.ExchangeEvents, results.DirectoryAuditEvents,
      results.SharePointEvents, results.UrlClickEvents, results.DlpEvents,
      results.GeneralEvents, results.AzureActivityEvents, results.RiskDetectionEvents,
      results.SecurityAlertEvents, results.SecurityIncidentEvents, results.RiskyUserEvents,
      results.NonInteractiveSignInEvents,
    ].forEach(arr => {
      (arr || []).forEach(evt => {
        if (evt.IPAddress) nonMsgTraceIPs.add(evt.IPAddress);
        if (evt.InferredIP) nonMsgTraceIPs.add(evt.InferredIP);
        if (evt.AllResolvedIPs) {
          evt.AllResolvedIPs.split(',').map(ip => ip.trim()).filter(Boolean)
            .forEach(ip => nonMsgTraceIPs.add(ip));
        }
      });
    });

    const msgTraceIPs = new Set();
    (results.MessageTraceEvents || []).forEach(evt => {
      if (evt.IPAddress) msgTraceIPs.add(evt.IPAddress);
    });

    const emailOnly = new Set();
    msgTraceIPs.forEach(ip => {
      if (!nonMsgTraceIPs.has(ip)) emailOnly.add(ip);
    });
    return emailOnly;
  }, [results]);

  const emailOnlyIPCount = emailOnlyIPSet.size;

  // Device names that appear ANYWHERE in the case results (v1.147.0).
  //
  // Used to mark a device recovered from the non-interactive feed as "new to case" —
  // the `DESKTOP-D61DA9` shape: zero rows in the shared events table AND no `Add device`
  // audit event (that tenant's directory_audit coverage began 11 days after the device
  // was registered), so every audit-driven and sign-in-driven view is blind to it.
  //
  // Two sources, because a device can reach a case by either route: sign-in events carry
  // `DeviceHostname` directly, while a registration-only device appears solely inside a
  // directory-audit `RawLog`. The audit scan is a substring test over a handful of rows
  // (43 on the reference case), so it is cheap — and it is what correctly keeps
  // `DESKTOP-5C2048` OUT of the "new to case" set: it IS in the case, as two registration
  // rows with no activity and no IP. Overstating what is new would be its own small lie.
  // Session cross-reference for the non-interactive panel. Built from the UNFILTERED
  // `results` on purpose: the point is what the underlying record holds about a session,
  // not what the current filter bar shows. Depends on ipClassifications so the "N of these
  // are classified malicious" line tracks the analyst's own judgement live.
  const caseSessionIndex = useMemo(
    () => buildSessionIndex(results, ipClassifications),
    [results, ipClassifications],
  );

  // (from, to) -> { total, malicious } over the case's own events. The inner closure is
  // rebuilt only when `results` changes; classifications are passed per call so a
  // reclassification does not force the whole event scan again.
  const activityInWindowBase = useMemo(() => makeActivityInWindow(results), [results]);
  const activityInWindowFn = useMemo(
    () => (from, to) => activityInWindowBase(from, to, ipClassifications),
    [activityInWindowBase, ipClassifications],
  );

  const caseDeviceNames = useMemo(() => {
    const names = new Set();
    (results?.SignInEvents || []).forEach((evt) => {
      const n = (evt.DeviceHostname || '').trim().toUpperCase();
      if (n) names.add(n);
    });
    const candidates = (nonInteractive?.ips || []).flatMap((r) => r.devices || []);
    if (candidates.length > 0) {
      const audits = (results?.DirectoryAuditEvents || [])
        .map((e) => (e.RawLog || '')).join('|').toUpperCase();
      if (audits) {
        candidates.forEach((d) => {
          const n = String(d || '').trim().toUpperCase();
          if (n && !names.has(n) && audits.includes(n)) names.add(n);
        });
      }
    }
    return names;
  }, [results, nonInteractive]);

  // Sessions the analyst marked as the attacker's, from the sign-in that created them
  // (utils/sessionMarks.js, v1.190.0). Declared before everything below that reads it.
  const sessionMarkMap = useMemo(() => attackerSessionMarks(sessionClassifications), [sessionClassifications]);

  // The analyst's malicious addresses — the same set calculateAttackerWindow derives, needed
  // here BEFORE the window so the leg attribution below does not depend on it.
  const maliciousIpSet = useMemo(() => new Set(
    Object.entries(ipClassifications || {}).filter(([, s]) => s === 'malicious').map(([ip]) => ip)
  ), [ipClassifications]);

  // 🔴 Recovered sign-in leg attribution (v1.188.0;
  // Plans/2026-09-30_Recovered-Legs-Transaction-Attribution.md). Which "Recovered · no UPN"
  // MFA legs are the attacker's, decided by their sign-in TRANSACTION (CorrelationId), not by
  // shape. Its OWN memo, independent of the window, because the window can be null — and a
  // case whose only recovered legs are the victim's still needs their verdicts, to let the
  // Safe-IP pill hide them and to explain them (review finding R1). Built over ALL sign-ins,
  // dismissed included: dismissal must never un-resolve a leg's siblings.
  const legAttribution = useMemo(() => {
    if (!results) return null;
    return analyzeRecoveredLegs(results.SignInEvents || [], {
      maliciousSet: maliciousIpSet,
      attackerSessions: buildAttackerContextFrom(results, maliciousIpSet, null, sessionMarkMap).attackerSessions,
      identityIds: caseIdentityIds(results),
    });
  }, [results, maliciousIpSet, sessionMarkMap]);

  // Attacker window calculation (excludes dismissed events)
  const attackerWindow = useMemo(() => {
    if (!results) return null;
    return calculateAttackerWindow(
      results.SignInEvents || [],
      results.DirectoryAuditEvents || [],
      ipClassifications,
      tenantTokenLifetime,
      results,
      dismissedEventIds,
      { legAttribution, analystEvents: attackerEventMarks, attackerSessions: sessionMarkMap },
    );
  }, [results, ipClassifications, tenantTokenLifetime, dismissedEventIds, legAttribution, attackerEventMarks, sessionMarkMap]);

  // Records the analyst marked Safe (v1.189.8): set aside with the Safe IPs, without
  // classifying their address. Declared BEFORE filteredResults, which reads it.
  const safeMarkIds = useMemo(() => safeMarkedIds(attackerEventMarks), [attackerEventMarks]);

  // Evidence for judging the case user's OWN Teams meeting attendance (utils/teamsMeetings.js):
  // their attendance before the attacker's first recorded activity, and their sign-ins. Built
  // from the UNFILTERED results so a filter pill cannot shrink the comparison.
  const meetingContext = useMemo(() => {
    if (!results) return null;
    return {
      baseline: buildMeetingDeviceBaseline(results.GeneralEvents || [], attackerWindow?.firstAttackerActivity || null),
      signIns: results.SignInEvents || [],
      nonInteractive: results.NonInteractiveSignInEvents || [],
    };
  }, [results, attackerWindow]);

  // "No attacker access found" attestation gate (v1.182.0). Reviewed = the non-interactive
  // feed was retrieved (collected key present) AND the analyst ran the Non-interactive panel
  // (a persisted envelope OR a live result in memory).
  const nonInteractiveReviewed = Boolean(results?.NonInteractiveCollected)
    && (Boolean(nonInteractive) || Boolean(caseData?.nonInteractiveIps));
  const noAccessGate = useMemo(() => computeNoAccessEligibility({
    attackerWindow,
    ipClassifications,
    signInEvents: results?.SignInEvents || [],
    nonInteractiveEvents: results?.NonInteractiveSignInEvents || [],
    nonInteractiveReviewed,
  }), [attackerWindow, ipClassifications, results, nonInteractiveReviewed]);

  // Compromised-session map — { sessionId -> earliest ISO event time from a
  // malicious IP under that session }. Sign-in `SessionId` and Exchange/SharePoint
  // `AADSessionId` are treated as a unified namespace (Microsoft typically
  // issues the same GUID for both layers of the same logical session).
  // Empty when no IPs classified Malicious and no session is marked — the pre-compromise
  // filter and the session-compromised badge are both no-ops in that case.
  //
  // 🔴 A session the analyst MARKED as the attacker's (v1.190.0) starts at its FIRST row in
  // any collection, not its first malicious touch: the mark says the session was stolen from
  // the sign-in that created it, so nothing on it is the user's "legitimate prior use" and
  // "Pre-compromise hidden" must not hide the capture.
  const compromisedSessions = useMemo(() => {
    const map = new Map();
    if (!results) return map;
    for (const [sid, ts] of markedSessionStarts(results, sessionMarkMap)) map.set(sid, ts);
    if (maliciousIpSet.size === 0) return map;
    const malSet = maliciousIpSet;
    const collect = (events) => {
      if (!events) return;
      for (const evt of events) {
        const sid = evt.SessionId || evt.AADSessionId;
        if (!sid) continue;
        const ip = evt.IPAddress || evt.InferredIP || '';
        if (!ip || !malSet.has(ip)) continue;
        const ts = evt.EventTimeUTC;
        if (!ts) continue;
        const cur = map.get(sid);
        if (!cur || ts < cur) map.set(sid, ts);
      }
    };
    collect(results.SignInEvents);
    collect(results.ExchangeEvents);
    collect(results.SharePointEvents);
    collect(results.MessageTraceEvents);
    collect(results.UrlClickEvents);
    collect(results.GeneralEvents);
    collect(results.AzureActivityEvents);
    collect(results.RiskDetectionEvents);
    collect(results.SecurityAlertEvents);
    return map;
  }, [results, maliciousIpSet, sessionMarkMap]);

  // Safe-session set — sessionIds the analyst has explicitly classified as
  // `safe`. Compromised wins: a session that's also in `compromisedSessions`
  // is excluded from this set, so its classification is preserved in storage
  // but ignored by the filter while the malicious-IP touch stands.
  const safeSessionSet = useMemo(() => {
    const set = new Set();
    for (const [sid, status] of Object.entries(sessionClassifications || {})) {
      if (status !== 'safe') continue;
      if (compromisedSessions.has(sid)) continue;
      set.add(sid);
    }
    return set;
  }, [sessionClassifications, compromisedSessions]);

  // Safe-IP-anchored session set — sessions first seen from an analyst-Safe IP and
  // never touched by a malicious IP. Powers the "Extend safe to sessions" filter:
  // collapses session-bound events (e.g. service-IP reads) that provably belong to a
  // session the user owned. Live-derived (no persistence), so it auto-updates as IP
  // classifications change; contested sessions are excluded via compromisedSessions.
  const safeIpSessionSet = useMemo(
    () => deriveSafeIpSessionSet(results, effectiveSafeIPSet, compromisedSessions),
    [results, effectiveSafeIPSet, compromisedSessions]
  );

  // Distinct (non-compromised) session ids seen across the case, with event
  // counts. Powers the AttackerWindowPanel "Other Sessions Seen" bulk list.
  const otherSessionsSummary = useMemo(() => {
    if (!results) return [];
    const counts = new Map();
    const tally = (events) => {
      if (!events) return;
      for (const evt of events) {
        const sid = evt.SessionId || evt.AADSessionId;
        if (!sid) continue;
        if (compromisedSessions.has(sid)) continue;
        counts.set(sid, (counts.get(sid) || 0) + 1);
      }
    };
    tally(results.SignInEvents);
    tally(results.ExchangeEvents);
    tally(results.SharePointEvents);
    tally(results.MessageTraceEvents);
    tally(results.UrlClickEvents);
    tally(results.GeneralEvents);
    tally(results.AzureActivityEvents);
    tally(results.RiskDetectionEvents);
    tally(results.SecurityAlertEvents);
    return [...counts.entries()]
      .map(([sid, n]) => ({ sessionId: sid, eventCount: n, status: sessionStatusOf(sessionClassifications, sid) }))
      .sort((a, b) => b.eventCount - a.eventCount)
      .slice(0, 50);
  }, [results, compromisedSessions, sessionClassifications]);

  // Does this case contain any noisy Outlook background events? Gates the
  // "Outlook noise" toggle so it only appears when there's something to hide.
  const hasOutlookNoise = useMemo(
    () => (results?.GeneralEvents || []).some(evt => OUTLOOK_NOISE_OPS.has(evt.Operation)),
    [results]
  );

  // Load the portal alerts about this user for the case window. Keyed on the identity the
  // run resolved, so a re-run that resolves more addresses picks up alerts recorded under
  // them. Waits for the run results: before the first run there is no timeline to show.
  const caseAlertTenantId = caseData?.tenantId || '';
  const caseAlertStart = (caseData?.startDate || '').substring(0, 10);
  const caseAlertEnd = (caseData?.endDate || '').substring(0, 10);
  const caseAlertUpn = caseData?.userPrincipalName || '';
  const caseAlertIdentity = results?.IdentityResolution || null;
  const hasCaseResults = !!results;
  useEffect(() => {
    let cancelled = false;
    const win = caseAlertWindow(caseAlertStart, caseAlertEnd);
    const users = caseAlertIdentifiers(caseAlertUpn, caseAlertIdentity);
    if (!hasCaseResults || !caseAlertTenantId || !win || users.length === 0) {
      setCaseAlertRows([]);
      setCaseAlertsError('');
      return undefined;
    }
    fetchCaseUserAlerts(caseAlertTenantId, { users, from: win.from, to: win.to })
      .then((alerts) => {
        if (cancelled) return;
        setCaseAlertRows(toCaseAlertEvents(alerts));
        setCaseAlertsError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setCaseAlertRows([]);
        setCaseAlertsError(err?.message || 'Request failed');
      });
    return () => { cancelled = true; };
  }, [hasCaseResults, caseAlertTenantId, caseAlertStart, caseAlertEnd, caseAlertUpn, caseAlertIdentity]);

  const filteredResults = useMemo(() => {
    if (!results) return { filtered: results, serviceExcluded: null, safeExcluded: null };

    // --- Phase 1: Service IP filtering ---
    let signIn = results.SignInEvents || [];
    let exchange = results.ExchangeEvents || [];
    let ips = results.IPAddresses || [];
    let directory = results.DirectoryAuditEvents || [];
    let sharePoint = results.SharePointEvents || [];
    let messageTrace = results.MessageTraceEvents || [];
    let urlClicks = results.UrlClickEvents || [];
    let dlp = results.DlpEvents || [];
    // --- Phase 0: stamp the flagged-phish quarantine release ------------------
    // 🔴 BEFORE ANY FILTER RUNS. The stamped fields (`_releasedSender` /
    // `_releasedSubject` / `_releasedFalsePositive`) are what both copies of
    // generateDescription read, so stamping only the rows the re-injection below adds
    // back would render the SAME event two different ways depending on which pill was
    // engaged. See utils/quarantineRelease.js for why the correlation is exact.
    const flaggedPhishIndex = flaggedPhishMessageIndex(phishingFlags, results.MessageTraceEvents);
    const releaseEvidence = collectReleaseEvidence(results.GeneralEvents);
    const stampedGeneral = stampFlaggedReleases(results.GeneralEvents || [], flaggedPhishIndex, releaseEvidence);

    let general = stampedGeneral;
    let azureActivity = results.AzureActivityEvents || [];
    let riskDetection = results.RiskDetectionEvents || [];
    let riskyUser = results.RiskyUserEvents || [];
    let secureScore = results.SecureScoreEvents || [];
    let securityAlert = (results.SecurityAlertEvents || []).filter(evt => evt.IsUserRelated);
    let securityIncident = (results.SecurityIncidentEvents || []).filter(evt => evt.IsUserRelated);
    // Collapsed non-interactive sign-in activity. Runs through EVERY filter below that the
    // other IP+session-bearing workloads do -- a pill that is visibly on and silently does
    // nothing to these rows is worse than one that is absent.
    // Portal detection alerts (utils/caseAlerts.js). Only DISMISSAL (Phase 5) applies:
    // they are the detection engine's own verdicts, so the IP / session / window pills,
    // which hide routine activity, must not hide them.
    let caseAlerts = caseAlertRows;
    let nonInteractiveEvents = results.NonInteractiveSignInEvents || [];  // NOT `nonInteractive` -- that name is the PANEL's envelope (useState, line ~287)

    // Service IP Events tab: collect events matching the manual service IP list for reference.
    // Service IPs are filtered from the main timeline via the Safe IPs path (they're in effectiveSafeIPSet).
    let serviceExcluded = null;
    if (serviceIPs.length > 0) {
      const isServiceIPEvent = (evt) => serviceIPSet.has(evt.IPAddress);
      serviceExcluded = {
        signInEvents: signIn.filter((evt) => isServiceIPEvent(evt)),
        exchangeEvents: exchange.filter((evt) => isServiceIPEvent(evt) && evt.IPResolutionMethod === 'Unresolved-ServiceIP'),
        sharePointEvents: sharePoint.filter((evt) => isServiceIPEvent(evt) && evt.IPResolutionMethod === 'Unresolved-ServiceIP'),
        ipAddresses: (results.IPAddresses || []).filter((ip) => serviceIPSet.has(ip.IPAddress)),
        totalExcluded: 0,
      };
      serviceExcluded.totalExcluded = serviceExcluded.signInEvents.length + serviceExcluded.exchangeEvents.length + serviceExcluded.sharePointEvents.length;
    }

    // --- Phase 1b: Email-only IPs are MARKED, never removed (v1.147.1) ---
    // This used to drop them from the IP Addresses table. That table is the case's
    // classification surface — an IP that is not in it cannot be marked Safe/Suspect/
    // Malicious at all, so hiding rows there removes the analyst's ability to act on
    // them rather than merely tidying the view. The toggle now annotates instead: rows
    // whose only appearance is in message trace carry an `IsEmailOnlyIP` flag the table
    // renders as a badge. No other surface consumed this filter, so nothing else changes.
    if (emailOnlyIPSet.size > 0) {
      ips = ips.map(ip => (
        emailOnlyIPSet.has(ip.IPAddress) ? { ...ip, IsEmailOnlyIP: true } : ip
      ));
    }

    // --- Phase 2: Safe IP filtering ---
    let safeExcluded = null;
    // Defined outside the if block so it can be reused after Phase 3b stripping
    const isFullySafe = (evt) => {
      if (!hideSafeIPs || effectiveSafeIPs.length === 0) return false;
      // Check multi-IP resolved events: use ResolvedIPCount OR IPResolutionMethod as signal
      const isMultiIP = (evt.ResolvedIPCount > 1 || (evt.IPResolutionMethod || '').includes('MultiIP'));
      if (isMultiIP && evt.AllResolvedIPs) {
        const resolvedIPs = evt.AllResolvedIPs.split(',').map(ip => ip.trim()).filter(Boolean);
        return resolvedIPs.length > 0 && resolvedIPs.every(ip => effectiveSafeIPSet.has(ip));
      }
      return effectiveSafeIPSet.has(evt.IPAddress);
    };
    // Override gate: post-compromise session-tied events bypass implicit hiding
    // (Safe IPs / Service IPs / Email-only / etc.) UNLESS the analyst has
    // explicitly classified the IP as `safe` or dismissed the row. Defined here
    // (not inside the `if (hideSafeIPs)` block) so it's available to the count
    // computation and any later phases that want to consult it.
    const overrideKeep = (evt) =>
      shouldOverrideHideForSessionCompromise(evt, compromisedSessions, ipClassifications, dismissedEventIds);
    if (hideSafeIPs && effectiveSafeIPs.length > 0) {
      // Recovered / phishing-kit sign-in legs whose verdict is attacker, suspected or
      // unknown are NEVER hidden by the Safe-IP pill, exactly like Directory Audit / Risk
      // Detection: a kit landing sits on the VICTIM'S OWN address (they were relayed through
      // the attacker's proxy), which is precisely why it lands in the safe set, and hiding it
      // would remove how the credentials were captured. 🔴 A BENIGN leg (v1.188.0) — the
      // user's own MFA sub-step on the user's own session — follows the ordinary IP rules:
      // before v1.188.0 every UnattributedRecovery leg was exempt, so marking the address Safe
      // could not hide 52 of the victim's own MFA pushes (IR-20260930-mendozap-001). Analyst
      // dismissal (Phase 5) still applies.
      const safeExclSignIn = signIn.filter((evt) => effectiveSafeIPSet.has(evt.IPAddress) && !overrideKeep(evt) && !isVisibleStandaloneLeg(evt, legAttribution));
      const safeExclExchange = exchange.filter((evt) => isFullySafe(evt) && !overrideKeep(evt));
      // Directory Audit events are NEVER hidden — they show remediation actions (account disable, password reset)
      const safeExclSharePoint = sharePoint.filter((evt) => isFullySafe(evt) && !overrideKeep(evt));
      // For event types with inferred IPs, use effective IP (actual or inferred) for safe filtering
      const getEffectiveIP = (evt) => evt.IPAddress || evt.InferredIP || '';
      const safeExclMsgTrace = messageTrace.filter((evt) => { const ip = getEffectiveIP(evt); return ip && effectiveSafeIPSet.has(ip) && !overrideKeep(evt); });
      const safeExclUrlClicks = urlClicks.filter((evt) => {
        // Only hide URL clicks classified as benign when safe IPs are hidden;
        // unclassified/confirmed/suspected URL clicks always stay visible
        const cls = urlClassifications[evt.EventId] || 'default';
        return cls === 'benign' && !overrideKeep(evt);
      });
      const safeExclDlp = dlp.filter((evt) => { const ip = getEffectiveIP(evt); return ip && effectiveSafeIPSet.has(ip) && !overrideKeep(evt); });
      const safeExclGeneral = general.filter((evt) => { const ip = getEffectiveIP(evt); return ip && effectiveSafeIPSet.has(ip) && !overrideKeep(evt); });
      const safeExclAzureActivity = azureActivity.filter((evt) => { const ip = getEffectiveIP(evt); return ip && effectiveSafeIPSet.has(ip) && !overrideKeep(evt); });
      const safeExclNonInteractive = nonInteractiveEvents.filter((evt) => { const ip = getEffectiveIP(evt); return ip && effectiveSafeIPSet.has(ip) && !overrideKeep(evt); });
      // Risk Detection + Security Alert events are NEVER hidden by the Safe IPs filter —
      // like Directory Audit, they carry critical detection context regardless of IP.

      signIn = signIn.filter((evt) => overrideKeep(evt) || isVisibleStandaloneLeg(evt, legAttribution) || !effectiveSafeIPSet.has(evt.IPAddress));
      exchange = exchange.filter((evt) => overrideKeep(evt) || !isFullySafe(evt));
      // directory is NOT filtered — always visible
      sharePoint = sharePoint.filter((evt) => overrideKeep(evt) || !isFullySafe(evt));
      messageTrace = messageTrace.filter((evt) => { if (overrideKeep(evt)) return true; const ip = getEffectiveIP(evt); return !ip || !effectiveSafeIPSet.has(ip); });
      urlClicks = urlClicks.filter((evt) => {
        if (overrideKeep(evt)) return true;
        const cls = urlClassifications[evt.EventId] || 'default';
        return cls !== 'benign';
      });
      dlp = dlp.filter((evt) => { if (overrideKeep(evt)) return true; const ip = getEffectiveIP(evt); return !ip || !effectiveSafeIPSet.has(ip); });
      general = general.filter((evt) => { if (overrideKeep(evt)) return true; const ip = getEffectiveIP(evt); return !ip || !effectiveSafeIPSet.has(ip); });
      azureActivity = azureActivity.filter((evt) => { if (overrideKeep(evt)) return true; const ip = getEffectiveIP(evt); return !ip || !effectiveSafeIPSet.has(ip); });
      // Non-interactive rows ARE filtered by safe IPs, like signIn: known-good token
      // refresh is deliberately VISIBLE BY DEFAULT as the comparison baseline for unknown
      // activity in the same hour, and this pill is the reversible way to drop it.
      nonInteractiveEvents = nonInteractiveEvents.filter((evt) => { if (overrideKeep(evt)) return true; const ip = getEffectiveIP(evt); return !ip || !effectiveSafeIPSet.has(ip); });
      // riskDetection + securityAlert NOT filtered by safe IPs — always visible (see above)

      const safeExclIPs = ips.filter((ip) => effectiveSafeIPSet.has(ip.IPAddress));

      safeExcluded = {
        signInEvents: safeExclSignIn,
        exchangeEvents: safeExclExchange,
        directoryAuditEvents: [],
        sharePointEvents: safeExclSharePoint,
        messageTraceEvents: safeExclMsgTrace,
        urlClickEvents: safeExclUrlClicks,
        dlpEvents: safeExclDlp,
        generalEvents: safeExclGeneral,
        azureActivityEvents: safeExclAzureActivity,
        riskDetectionEvents: [],
        securityAlertEvents: [],
        nonInteractiveSignInEvents: safeExclNonInteractive,
        ipAddresses: safeExclIPs,
        totalExcluded: safeExclSignIn.length + safeExclExchange.length +
                       safeExclSharePoint.length +
                       safeExclMsgTrace.length + safeExclUrlClicks.length +
                       safeExclDlp.length + safeExclGeneral.length +
                       safeExclAzureActivity.length + safeExclNonInteractive.length,
      };
      // Keep safe IPs visible in IP table (don't filter ips array)
    }

    // --- Phase 3: Attacker window post-cutoff filtering ---
    let postWindowCount = 0;
    if (attackerWindow && !attackerWindow.isIndeterminate && attackerWindow.globalCutoff) {
      const malSet = new Set(attackerWindow.maliciousIPs);
      const cutoff = attackerWindow.globalCutoff;

      // Count post-window events (always, for badge) — exclude directory audit
      const countableArrays = [
        signIn, exchange, sharePoint, messageTrace, urlClicks,
        dlp, general, azureActivity, riskDetection, riskyUser,
        securityAlert, securityIncident,
      ];
      countableArrays.forEach((arr) => {
        arr.forEach((evt) => {
          if (isPostWindowEvent(evt, cutoff, malSet)) postWindowCount++;
        });
      });

      // When toggle is ON, filter out post-window events (directory audit never hidden)
      if (hidePostWindow) {
        signIn = signIn.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        exchange = exchange.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        sharePoint = sharePoint.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        messageTrace = messageTrace.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        urlClicks = urlClicks.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        dlp = dlp.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        general = general.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        azureActivity = azureActivity.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        riskDetection = riskDetection.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        nonInteractiveEvents = nonInteractiveEvents.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        riskyUser = riskyUser.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        securityAlert = securityAlert.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
        securityIncident = securityIncident.filter((evt) => !isPostWindowEvent(evt, cutoff, malSet));
      }

      // --- Phase 3b: Strip expired malicious IPs from AllResolvedIPs on post-window events ---
      // After the cutoff, attacker tokens have expired — their IPs should not appear as
      // possible sources in multi-IP session-resolved events.
      const stripExpiredAttackerIPs = (evt) => {
        if (!evt.EventTimeUTC || evt.EventTimeUTC <= cutoff) return evt;
        if (!evt.AllResolvedIPs) return evt;

        const resolvedIPs = evt.AllResolvedIPs.split(',').map(ip => ip.trim());
        const cleaned = resolvedIPs.filter(ip => !malSet.has(ip));

        if (cleaned.length === resolvedIPs.length) return evt; // no malicious IPs present

        return {
          ...evt,
          AllResolvedIPs: cleaned.join(', '),
          ResolvedIPCount: cleaned.length,
          ResolvedUserIP: cleaned.length === 1 ? cleaned[0] :
            (evt.ResolvedUserIP && !malSet.has(evt.ResolvedUserIP) ? evt.ResolvedUserIP : ''),
        };
      };

      signIn = signIn.map(stripExpiredAttackerIPs);
      exchange = exchange.map(stripExpiredAttackerIPs);
      sharePoint = sharePoint.map(stripExpiredAttackerIPs);
      // directory audit excluded — remediation events retain original data
      messageTrace = messageTrace.map(stripExpiredAttackerIPs);
      urlClicks = urlClicks.map(stripExpiredAttackerIPs);
      dlp = dlp.map(stripExpiredAttackerIPs);
      general = general.map(stripExpiredAttackerIPs);
      azureActivity = azureActivity.map(stripExpiredAttackerIPs);

      // Re-filter: after stripping malicious IPs, some multi-IP events may now have
      // only safe IPs remaining — these should be hidden by the safe IP filter
      // (unless they qualify for the post-compromise session override).
      if (hideSafeIPs && effectiveSafeIPs.length > 0) {
        exchange = exchange.filter((evt) => overrideKeep(evt) || !isFullySafe(evt));
        sharePoint = sharePoint.filter((evt) => overrideKeep(evt) || !isFullySafe(evt));
      }
      riskDetection = riskDetection.map(stripExpiredAttackerIPs);
      nonInteractiveEvents = nonInteractiveEvents.map(stripExpiredAttackerIPs);
      securityAlert = securityAlert.map(stripExpiredAttackerIPs);
      securityIncident = securityIncident.map(stripExpiredAttackerIPs);
    }

    // --- Phase 3.4: Before-the-window filtering ---
    // The counterpart to Phase 3, at the other end of the window. Phase 3.5 below is
    // SESSION-scoped and cannot see an event with no session id or on a session the
    // attacker never touched: on IR-20260826-gwoodward-001 that left 98 SharePoint and
    // 100 Exchange rows from before the attacker arrived sitting in the reviewed view,
    // because the analyst had (correctly) identified their addresses as the real user's.
    //
    // Directory audit is excluded, exactly as in Phase 3 -- a remediation action stays
    // visible whenever it happened. Counted always so the pill can state its size even
    // while disengaged. The analyst's entry-point rows (flagged phish, confirmed or
    // suspected click) are exempt too: they precede the window by definition (v1.194.5).
    let preWindowCount = 0;
    if (attackerWindow && !attackerWindow.isIndeterminate && attackerWindow.firstAttackerActivity) {
      const malSet = new Set(attackerWindow.maliciousIPs);
      const start = attackerWindow.firstAttackerActivity;
      const entryIds = analystEntryPointIds(phishingFlags, urlClassifications);
      const preWindowArrays = [
        signIn, exchange, sharePoint, messageTrace, urlClicks,
        dlp, general, azureActivity, riskDetection, riskyUser,
        securityAlert, securityIncident, nonInteractiveEvents,
      ];
      preWindowArrays.forEach((arr) => {
        arr.forEach((evt) => {
          if (isPreWindowEvent(evt, start, malSet, entryIds)) preWindowCount++;
        });
      });

      if (hidePreWindow) {
        signIn = signIn.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        exchange = exchange.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        sharePoint = sharePoint.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        messageTrace = messageTrace.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        urlClicks = urlClicks.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        dlp = dlp.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        general = general.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        azureActivity = azureActivity.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        riskDetection = riskDetection.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        riskyUser = riskyUser.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        securityAlert = securityAlert.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        securityIncident = securityIncident.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        nonInteractiveEvents = nonInteractiveEvents.filter((evt) => !isPreWindowEvent(evt, start, malSet, entryIds));
        // Directory audit never filtered -- remediation actions always visible.
      }
    }

    // --- Phase 3.5: Pre-compromise session filtering ---
    // Once the analyst classifies any IP as Malicious, every session it touched
    // gets a "compromise start" timestamp (MIN event time from that malicious IP
    // under the session). Events under those sessions BEFORE that time are
    // legitimate prior session use and should be hidden by default.
    // (No-op while compromisedSessions is empty.)
    let preCompromiseCount = 0;
    if (compromisedSessions.size > 0) {
      const preCompCountArrays = [
        signIn, exchange, sharePoint, messageTrace, urlClicks,
        dlp, general, azureActivity, riskDetection, riskyUser,
        securityAlert,
      ];
      preCompCountArrays.forEach((arr) => {
        arr.forEach((evt) => {
          if (isPreCompromiseSessionEvent(evt, compromisedSessions)) preCompromiseCount++;
        });
      });

      if (hidePreCompromise) {
        signIn = signIn.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        exchange = exchange.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        sharePoint = sharePoint.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        messageTrace = messageTrace.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        urlClicks = urlClicks.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        dlp = dlp.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        general = general.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        azureActivity = azureActivity.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        riskDetection = riskDetection.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        nonInteractiveEvents = nonInteractiveEvents.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        riskyUser = riskyUser.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        securityAlert = securityAlert.filter((evt) => !isPreCompromiseSessionEvent(evt, compromisedSessions));
        // Directory audit never filtered — remediation actions always visible.
      }
    }

    // --- Phase 3.6: Safe-session filtering ---
    // Mirror image of Phase 3.5: events whose SessionId / AADSessionId is in
    // the analyst-marked safe set (and NOT also compromised — see
    // safeSessionSet build) get hidden when `hideSafeSessions` is true.
    let safeSessionCount = 0;
    if (safeSessionSet.size > 0) {
      const isSafeSessionEvent = (evt) => {
        const sid = evt.SessionId || evt.AADSessionId;
        return !!(sid && safeSessionSet.has(sid));
      };
      const safeSessionCountArrays = [
        signIn, exchange, sharePoint, messageTrace, urlClicks,
        dlp, general, azureActivity, riskDetection, riskyUser,
        securityAlert,
      ];
      safeSessionCountArrays.forEach((arr) => {
        arr.forEach((evt) => { if (isSafeSessionEvent(evt)) safeSessionCount++; });
      });

      if (hideSafeSessions) {
        signIn = signIn.filter((evt) => !isSafeSessionEvent(evt));
        exchange = exchange.filter((evt) => !isSafeSessionEvent(evt));
        sharePoint = sharePoint.filter((evt) => !isSafeSessionEvent(evt));
        messageTrace = messageTrace.filter((evt) => !isSafeSessionEvent(evt));
        urlClicks = urlClicks.filter((evt) => !isSafeSessionEvent(evt));
        dlp = dlp.filter((evt) => !isSafeSessionEvent(evt));
        general = general.filter((evt) => !isSafeSessionEvent(evt));
        azureActivity = azureActivity.filter((evt) => !isSafeSessionEvent(evt));
        riskDetection = riskDetection.filter((evt) => !isSafeSessionEvent(evt));
        nonInteractiveEvents = nonInteractiveEvents.filter((evt) => !isSafeSessionEvent(evt));
        riskyUser = riskyUser.filter((evt) => !isSafeSessionEvent(evt));
        securityAlert = securityAlert.filter((evt) => !isSafeSessionEvent(evt));
        // Directory audit never filtered — remediation actions always visible.
      }
    }

    // --- Phase 3.7: Extend-safe-to-sessions filtering (v1.89.0) ---
    // Hide NON-sign-in events whose session is safe-IP-anchored (safeIpSessionSet =
    // first seen from an analyst-Safe IP, never touched a malicious IP). Sign-ins are
    // the session's location evidence AND the anchor, so they are NEVER hidden here.
    // An event whose own IP the analyst flagged suspect/malicious also stays visible.
    let safeIpSessionCount = 0;
    if (safeIpSessionSet.size > 0) {
      const isSafeIpSessionEvent = (evt) => {
        const sid = evt.SessionId || evt.AADSessionId;
        if (!sid || !safeIpSessionSet.has(sid)) return false;
        const ip = evt.IPAddress || evt.ResolvedUserIP || evt.InferredIP || '';
        const cls = ip ? ipClassifications[ip] : null;
        if (cls === 'suspect' || cls === 'malicious') return false;
        return true;
      };
      [exchange, sharePoint, messageTrace, urlClicks, dlp, general, azureActivity, riskDetection, riskyUser, securityAlert]
        .forEach((arr) => { arr.forEach((evt) => { if (isSafeIpSessionEvent(evt)) safeIpSessionCount++; }); });

      if (hideSafeIpSessions) {
        exchange = exchange.filter((evt) => !isSafeIpSessionEvent(evt));
        sharePoint = sharePoint.filter((evt) => !isSafeIpSessionEvent(evt));
        messageTrace = messageTrace.filter((evt) => !isSafeIpSessionEvent(evt));
        urlClicks = urlClicks.filter((evt) => !isSafeIpSessionEvent(evt));
        dlp = dlp.filter((evt) => !isSafeIpSessionEvent(evt));
        general = general.filter((evt) => !isSafeIpSessionEvent(evt));
        azureActivity = azureActivity.filter((evt) => !isSafeIpSessionEvent(evt));
        riskDetection = riskDetection.filter((evt) => !isSafeIpSessionEvent(evt));
        nonInteractiveEvents = nonInteractiveEvents.filter((evt) => !isSafeIpSessionEvent(evt));
        riskyUser = riskyUser.filter((evt) => !isSafeIpSessionEvent(evt));
        securityAlert = securityAlert.filter((evt) => !isSafeIpSessionEvent(evt));
        // signIn intentionally NOT filtered (session-location evidence + anchor).
        // Directory audit never filtered — remediation actions always visible.
      }
    }

    // --- Phase 4: Failed events filtering ---
    const isFailed = (evt) => {
      const s = evt.ResultStatus?.toLowerCase();
      return s === 'failed' || s === 'failure';
    };
    const failedCountArrays = [
      signIn, exchange, sharePoint, messageTrace, urlClicks,
      dlp, general, azureActivity, riskDetection,
      securityAlert, securityIncident, nonInteractiveEvents,
    ];
    let failedEventCount = 0;
    failedCountArrays.forEach((arr) => {
      arr.forEach((evt) => { if (isFailed(evt)) failedEventCount++; });
    });

    if (hideFailedEvents) {
      signIn = signIn.filter((evt) => !isFailed(evt));
      exchange = exchange.filter((evt) => !isFailed(evt));
      sharePoint = sharePoint.filter((evt) => !isFailed(evt));
      messageTrace = messageTrace.filter((evt) => !isFailed(evt));
      urlClicks = urlClicks.filter((evt) => !isFailed(evt));
      dlp = dlp.filter((evt) => !isFailed(evt));
      general = general.filter((evt) => !isFailed(evt));
      azureActivity = azureActivity.filter((evt) => !isFailed(evt));
      riskDetection = riskDetection.filter((evt) => !isFailed(evt));
      // Works because the runbook stamps ResultStatus on these rows; a Mixed row maps to
      // Success on purpose, so a row containing successful auths is never hidden here.
      nonInteractiveEvents = nonInteractiveEvents.filter((evt) => !isFailed(evt));
      securityAlert = securityAlert.filter((evt) => !isFailed(evt));
      securityIncident = securityIncident.filter((evt) => !isFailed(evt));
      // directory audit is NOT filtered — remediation actions always visible
    }

    // --- Phase 4.5a: DLP events filtering ---
    // DLP rule matches appear both as dedicated DlpEvents AND as Exchange events with Operation='DlpRuleMatch'
    const isDlpEvent = (evt) => evt.Operation === 'DlpRuleMatch';
    const dlpEventCount = dlp.length + exchange.filter(isDlpEvent).length;
    if (hideDlpEvents) {
      dlp = [];
      exchange = exchange.filter((evt) => !isDlpEvent(evt));
    }

    // --- Phase 4.5a2: Unresolved-service-IP mail-read / attachment-access filtering ---
    // MailItemsAccessed and AttachmentAccess events whose IP never resolved past the Microsoft
    // service proxy are high-volume noise. Hide them temporarily so the analyst can find
    // diagnosable events faster.
    const unresolvedReadOps = new Set(['MailItemsAccessed', 'AttachmentAccess']);
    // A row whose IP was only GUESSED (utils/guessedIp.js) never resolved either -- its
    // recorded address is the proxy -- so it belongs to the same pill.
    const isUnresolvedMailAccess = (evt) =>
      unresolvedReadOps.has(evt.Operation)
        && (evt.IPResolutionMethod === 'Unresolved-ServiceIP' || Boolean(evt.GuessedIP));
    const unresolvedMailAccessCount = exchange.filter(isUnresolvedMailAccess).length;
    if (hideUnresolvedMailAccess) {
      exchange = exchange.filter((evt) => !isUnresolvedMailAccess(evt));
    }

    // --- Phase 4.5b: Lookback correlation sign-in filtering ---
    // Count from RAW results (before any filters strip them) so the toggle always appears
    const allRawSignIns = results.SignInEvents || [];
    const totalCorrelationCount = allRawSignIns.filter(evt => evt.IsCorrelationEvent === true).length;

    // Re-inject correlation events that were removed by upstream filters (service IP, safe IP)
    // so they can be shown via the lookback toggle.
    // However, do NOT re-inject events that should remain hidden by analyst-driven
    // upstream filters: Safe IPs (existing), Pre-compromise session, Safe session
    // (v1.53.x), or Failed events (v1.179.6). Without these checks, lookback would
    // silently bypass those filters — e.g. a cluster of Failed correlation sign-ins on a
    // classified attacker IP re-appears (and rolls into an "all failed" access period)
    // even though the analyst engaged "Failed hidden".
    if (totalCorrelationCount > 0) {
      const currentIds = new Set(signIn.map(evt => evt.EventId));
      const missingCorrelation = allRawSignIns.filter(
        evt => evt.IsCorrelationEvent === true &&
               !currentIds.has(evt.EventId) &&
               !(hideSafeIPs && effectiveSafeIPSet.has(evt.IPAddress)) &&
               !(hidePreCompromise && isPreCompromiseSessionEvent(evt, compromisedSessions)) &&
               !(hideFailedEvents && isFailed(evt)) &&
               !(hideSafeSessions && (() => {
                 const sid = evt.SessionId || evt.AADSessionId;
                 return !!(sid && safeSessionSet.has(sid));
               })())
      );
      if (missingCorrelation.length > 0) {
        signIn = [...signIn, ...missingCorrelation];
      }
    }

    // --- Phase 4.5c: Re-inject the flagged-phish quarantine release ----------
    // The release of the message the analyst flagged as the phish is the most
    // consequential USER action a case like this holds, and it is dropped by the implicit
    // IP filters: its only address is an inferred Microsoft IP classified safe. The
    // report's canonical filter state has every pill engaged, so without this the finding
    // cannot reach a report however it is worded.
    //
    // 🔴 HERE, not at the end of the memo: after the seven hide phases and BEFORE Phase 5
    // dismissal, so an EXPLICITLY dismissed release still hides in `hidden` mode and
    // `only` mode does not gain a row nobody dismissed. The exception defeats an implicit
    // filter, never an analyst's judgment.
    {
      const reinjected = stampAndReinjectFlaggedReleases({ stampedGeneral, general, safeExcluded });
      general = reinjected.general;
      safeExcluded = reinjected.safeExcluded;
    }

    // Count correlation events that would be hidden (excludes those with classified IPs)
    const isHideableLookback = (evt) =>
      evt.IsCorrelationEvent === true &&
      (!ipClassifications[evt.IPAddress] || ipClassifications[evt.IPAddress] === 'default');
    const lookbackEventCount = signIn.filter(isHideableLookback).length;

    if (hideLookback) {
      signIn = signIn.filter((evt) => !isHideableLookback(evt));
    }

    // --- Phase 4.6: rows the analyst marked Safe (v1.189.8) ---
    // A per-ROW Safe, so a Microsoft service address (or any shared one) need not be marked
    // Safe for the whole case to set aside the one row that is the user's. Hidden whenever
    // "Safe IPs hidden" is engaged, and listed on the Safe IP Events tab like the rest.
    // 🔴 HERE, after every re-injecting phase (lookback 4.5b, flagged release 4.5c) and
    // before dismissal: an explicit per-row judgment must not be undone by an implicit
    // exception. It applies to directory audit, risk detections and alerts too -- those are
    // exempt from the IP rule because an ADDRESS says nothing about them, but this is the
    // analyst's judgment of the row itself.
    let safeMarkedRowCount = 0;
    if (hideSafeIPs && safeMarkIds.size > 0) {
      const empty = {
        signInEvents: [], exchangeEvents: [], directoryAuditEvents: [], sharePointEvents: [],
        messageTraceEvents: [], urlClickEvents: [], dlpEvents: [], generalEvents: [],
        azureActivityEvents: [], riskDetectionEvents: [], securityAlertEvents: [],
        nonInteractiveSignInEvents: [], ipAddresses: [], totalExcluded: 0,
      };
      safeExcluded = { ...empty, ...(safeExcluded || {}) };
      const take = (arr, key) => {
        const keep = [];
        const out = [];
        for (const evt of arr) (safeMarkIds.has(evt?.EventId) ? out : keep).push(evt);
        if (out.length) {
          safeExcluded[key] = [...(safeExcluded[key] || []), ...out];
          safeExcluded.totalExcluded += out.length;
          safeMarkedRowCount += out.length;
        }
        return keep;
      };
      signIn = take(signIn, 'signInEvents');
      exchange = take(exchange, 'exchangeEvents');
      directory = take(directory, 'directoryAuditEvents');
      sharePoint = take(sharePoint, 'sharePointEvents');
      messageTrace = take(messageTrace, 'messageTraceEvents');
      urlClicks = take(urlClicks, 'urlClickEvents');
      dlp = take(dlp, 'dlpEvents');
      general = take(general, 'generalEvents');
      azureActivity = take(azureActivity, 'azureActivityEvents');
      riskDetection = take(riskDetection, 'riskDetectionEvents');
      securityAlert = take(securityAlert, 'securityAlertEvents');
      nonInteractiveEvents = take(nonInteractiveEvents, 'nonInteractiveSignInEvents');
    }

    // --- Phase 5: Dismissed events filtering ---
    const isDismissed = (evt) => dismissedEventIds.has(evt.EventId);
    const dismissedCountArrays = [
      signIn, exchange, directory, sharePoint, messageTrace, urlClicks,
      dlp, general, azureActivity, riskDetection, riskyUser,
      securityAlert, securityIncident, caseAlerts,
    ];
    let dismissedEventCount = 0;
    dismissedCountArrays.forEach((arr) => {
      arr.forEach((evt) => { if (isDismissed(evt)) dismissedEventCount++; });
    });

    if (dismissedMode === 'hidden' && dismissedEventIds.size > 0) {
      signIn = signIn.filter((evt) => !isDismissed(evt));
      exchange = exchange.filter((evt) => !isDismissed(evt));
      directory = directory.filter((evt) => !isDismissed(evt));
      sharePoint = sharePoint.filter((evt) => !isDismissed(evt));
      messageTrace = messageTrace.filter((evt) => !isDismissed(evt));
      urlClicks = urlClicks.filter((evt) => !isDismissed(evt));
      dlp = dlp.filter((evt) => !isDismissed(evt));
      general = general.filter((evt) => !isDismissed(evt));
      azureActivity = azureActivity.filter((evt) => !isDismissed(evt));
      riskDetection = riskDetection.filter((evt) => !isDismissed(evt));
      nonInteractiveEvents = nonInteractiveEvents.filter((evt) => !isDismissed(evt));
      riskyUser = riskyUser.filter((evt) => !isDismissed(evt));
      securityAlert = securityAlert.filter((evt) => !isDismissed(evt));
      securityIncident = securityIncident.filter((evt) => !isDismissed(evt));
      caseAlerts = caseAlerts.filter((evt) => !isDismissed(evt));
      secureScore = secureScore.filter((evt) => !isDismissed(evt));
    } else if (dismissedMode === 'only' && dismissedEventIds.size > 0) {
      signIn = signIn.filter(isDismissed);
      exchange = exchange.filter(isDismissed);
      directory = directory.filter(isDismissed);
      sharePoint = sharePoint.filter(isDismissed);
      messageTrace = messageTrace.filter(isDismissed);
      urlClicks = urlClicks.filter(isDismissed);
      dlp = dlp.filter(isDismissed);
      general = general.filter(isDismissed);
      azureActivity = azureActivity.filter(isDismissed);
      riskDetection = riskDetection.filter(isDismissed);
      nonInteractiveEvents = nonInteractiveEvents.filter(isDismissed);
      riskyUser = riskyUser.filter(isDismissed);
      securityAlert = securityAlert.filter(isDismissed);
      securityIncident = securityIncident.filter(isDismissed);
      caseAlerts = caseAlerts.filter(isDismissed);
      secureScore = secureScore.filter(isDismissed);
    }

    // --- Risk Detections (All Events only) ---------------------------------
    // Microsoft's Identity Protection writes a risk-detection record ABOUT a sign-in, so
    // All Events rendered the same moment twice: on IR-20260818-Controller-001, 222 of
    // 227 risk rows join 1:1 to a sign-in on RequestId, at the identical second. They are
    // also the poorer of the pair -- 225 of 227 carry riskEventType 'generic' (Microsoft's
    // placeholder, rendered as the literal "performed generic") and RiskLevel/RiskDetail
    // are 'hidden' on ALL 227, so the row states no risk at all, while the sign-in it
    // duplicates already carries RiskState and a better-named RiskEventTypes.
    //
    // OFF by default (operator's call): the analyst reads them on first review and hides
    // them once they have. Hiding is safe because this pill scopes All Events ONLY -- the
    // Risk Detections sub-tab reads `baseResults` and always shows every row, including
    // the handful that are NOT generic and say something the sign-in does not.
    const riskDetectionAllEventsCount = riskDetection.length;
    if (hideRiskDetections) {
      riskDetection = [];
    }

    // The investigation's OWN IP list, captured BEFORE any stub is added. This is what
    // "new to this case" means -- an address the investigation itself never surfaced.
    // 🔴 It must be computed here, not inferred later from "was this row already present",
    // which is an ORDERING accident: v1.149.1 began stubbing non-interactive addresses
    // during reconciliation, so by the time the panel injection ran they were all present,
    // it pushed nothing, and the NonInteractiveOnly flag stopped being set at all -- the
    // "New to this case" filter went to 0 rows while every address was still listed.
    const runBlobIPSet = new Set((results.IPAddresses || []).map((ip) => ip.IPAddress));

    // --- Reconcile: ensure every IP seen anywhere in the case has an IP table entry ---
    // 🔴 Built from the UNFILTERED `results.*` arrays, NOT the filtered locals above.
    // The IP table is the analyst's CLASSIFICATION surface, so its row set must not move
    // when a timeline pill is toggled: a pill exists to tidy the timeline, and letting it
    // remove a row here takes away the ability to act on that IP instead (the same
    // principle already stated for the email-only toggle further up, which annotates
    // rather than filters). `ips` itself is never filtered; the stubs added below were the
    // pill-dependent part, because they were derived from the post-filter arrays.
    //
    // This is monotone and cannot surprise: the unfiltered set is exactly what the table
    // already showed with every pill off, so no row appears that was not appearing in some
    // pill state before -- rows simply stop DISAPPEARING. Reported on the DEV case with
    // "Safe IPs shown" vs hidden changing the visible addresses.
    //
    // `IsUserRelated` IS still applied to the two security workloads, because that is case
    // SCOPING (tenant-level alerts that are not about this user), not an analyst view
    // toggle -- it matches how those arrays are declared at the top of this memo.
    const ipSet = new Set(ips.map(ip => ip.IPAddress));
    const allVisibleEvents = [
      ...(results.SignInEvents || []), ...(results.ExchangeEvents || []),
      ...(results.DirectoryAuditEvents || []), ...(results.SharePointEvents || []),
      ...(results.MessageTraceEvents || []), ...(results.UrlClickEvents || []),
      ...(results.DlpEvents || []), ...(results.GeneralEvents || []),
      ...(results.AzureActivityEvents || []), ...(results.RiskDetectionEvents || []),
      ...(results.SecurityAlertEvents || []).filter(evt => evt.IsUserRelated),
      ...(results.SecurityIncidentEvents || []).filter(evt => evt.IsUserRelated),
      // Non-interactive rows too, so their IPs reach the IP table as classifiable rows
      // WITHOUT the analyst having to run the Non-interactive panel first. The panel's
      // own injection below still runs and is richer (feed counts + adjacency); this
      // path just guarantees the address is PRESENT to be excluded.
      // NOTE: the stub this produces has RiskLevel 0 and no provider/country, which
      // reads as a scored-clean IP rather than an unscored one -- the standing
      // 'not scored' render gap. 'Enrich unenriched' in the IP table fills them.
      ...(results.NonInteractiveSignInEvents || []),
    ];
    const missingIPs = new Set();
    allVisibleEvents.forEach(evt => {
      if (evt.IPAddress && !ipSet.has(evt.IPAddress)) missingIPs.add(evt.IPAddress);
      if (evt.InferredIP && !ipSet.has(evt.InferredIP)) missingIPs.add(evt.InferredIP);
    });
    if (missingIPs.size > 0) {
      // Check if the IP was in the original results but filtered out (e.g. service IP filter)
      const origIPMap = {};
      (results.IPAddresses || []).forEach(ip => { origIPMap[ip.IPAddress] = ip; });
      missingIPs.forEach(ip => {
        if (origIPMap[ip]) {
          ips.push(origIPMap[ip]);
        } else {
          ips.push({
            IPAddress: ip, City: '', State: '', Country: '',
            Provider: '', NetworkType: '', RiskLevel: 0,
            IsVPN: false, IsProxy: false, IsTOR: false,
            IsHosting: false, IsCompromised: false, IsAnonymous: false,
            EventCount: 0, IsKnownGood: false,
            DRMMMatched: false, DRMMHostname: '', DRMMUsername: '',
          });
        }
      });
    }

    // --- Flag addresses the investigation itself never surfaced (v1.149.2) ---
    // As of v1.149.0 the non-interactive feed is part of the RUN BLOB, so an address it
    // recovered is knowable without the analyst running the Non-interactive panel first.
    // Flag from the events, so "New to this case" works on a freshly-loaded case; the panel
    // block below then refines these rows with the feed's own counts and adjacency.
    // 🔴 Derived from `runBlobIPSet`, never from "was this row already here" -- see the note
    // on that set.
    const niEventIPs = new Set(
      (results.NonInteractiveSignInEvents || []).map((evt) => evt.IPAddress).filter(Boolean)
    );
    if (niEventIPs.size > 0) {
      // .map() also gives a fresh array, which keeps the panel block's .push() below safe.
      ips = ips.map((row) => (
        niEventIPs.has(row.IPAddress) && !runBlobIPSet.has(row.IPAddress)
          ? { ...row, NonInteractiveOnly: true }
          : row
      ));
    }

    // --- Inject IPs recovered from the NON-INTERACTIVE sign-in feed (v1.147.0) ---
    // Same mechanism as the reconciliation above, new source. These IPs have ZERO rows
    // in `silver_m365_unified_audit_events`, so no re-run of the investigation can
    // surface them — without this they stay invisible however long the analyst looks.
    //
    // EventCount stays 0 on purpose: it means "events in this case", and inflating it
    // with a non-interactive count would corrupt the case's own accounting. The
    // non-interactive figures live on `NonInteractive` and are rendered as such. That
    // does mean these rows sink under the default EventCount sort, which is why the
    // table auto-applies its "new to this case" filter after a fetch.
    if (nonInteractive?.ips?.length) {
      const niByIp = new Map();
      nonInteractive.ips.forEach((ni) => { if (ni?.ip) niByIp.set(ni.ip, ni); });
      // COPY-ON-WRITE. `ips` is still the `results.IPAddresses` array itself unless the
      // email-only filter reassigned it, so attaching the feed data by mutation would
      // write into component state from inside a useMemo. `.map()` gives a fresh array
      // (which also makes the `.push` below safe) and fresh row objects for the ones we
      // annotate; untouched rows keep their identity.
      ips = ips.map((row) => (
        niByIp.has(row.IPAddress)
          ? {
              ...row,
              NonInteractive: niByIp.get(row.IPAddress),
              // Flag it here as well as on the pushed rows below, because a row may now
              // already exist as a reconciliation stub. Keyed on the run blob's own list,
              // so it does not depend on whether this block ran before or after the stub.
              NonInteractiveOnly: !runBlobIPSet.has(row.IPAddress),
            }
          : row
      ));
      const presentIPs = new Set(ips.map((row) => row.IPAddress));
      nonInteractive.ips.forEach((ni) => {
        if (!ni?.ip || presentIPs.has(ni.ip)) return;
        presentIPs.add(ni.ip);
        ips.push({
          IPAddress: ni.ip,
          City: ni.enrichCity || '', State: '', Country: ni.enrichCountry || '',
          Provider: ni.provider || '', NetworkType: ni.networkType || '',
          // NULL, never 0. `riskLevel` is null when the IP has no enrichment row at
          // all, and 0 renders as a green "Low" badge — "checked, clean" for an address
          // nobody ever scored, on the same row whose panel says "(unenriched)".
          // getRiskLevelLabel(null) already renders a grey "Unknown", which is the truth.
          RiskLevel: ni.riskLevel ?? null,
          IsVPN: false, IsProxy: false, IsTOR: false,
          IsHosting: (ni.networkType || '').toLowerCase() === 'hosting',
          IsCompromised: false, IsAnonymous: false,
          EventCount: 0, IsKnownGood: false,
          DRMMMatched: false, DRMMHostname: '', DRMMUsername: '',
          NonInteractive: ni,
          // Always true on this path by construction (the address is absent from `ips`),
          // but derived from the same set as above so both paths share one definition.
          NonInteractiveOnly: !runBlobIPSet.has(ni.ip),
        });
      });
    }

    // --- Apply the persisted enrichment overlay to EVERY row, stubs included ---
    // Must run after the reconciliation and the non-interactive injection above, because
    // both create rows that are not in `results.IPAddresses` and therefore were never
    // reached by the one-shot hydration. Those are precisely the rows "Enrich unenriched"
    // targets, which is why their enrichment appeared to vanish on reload.
    if (Object.keys(enrichmentOverlay).length > 0) {
      ips = ips.map((row) => {
        const fresh = enrichmentOverlay[String(row.IPAddress || '').toLowerCase()];
        // Only fill rows that are still blank — a row already carrying enrichment from
        // the blob (or from a fresher in-session refresh) must not be overwritten by an
        // older saved snapshot.
        if (!fresh || row.Provider || row.Country) return row;
        return applyEnrichmentToRow(row, fresh);
      });
    }

    // --- Recalculate summary from final filtered sign-in events ---
    const summary = recalculateSummary(signIn);

    return {
      filtered: {
        ...results,
        SignInEvents: signIn,
        ExchangeEvents: exchange,
        DirectoryAuditEvents: directory,
        SharePointEvents: sharePoint,
        MessageTraceEvents: messageTrace,
        UrlClickEvents: urlClicks,
        DlpEvents: dlp,
        GeneralEvents: general,
        AzureActivityEvents: azureActivity,
        RiskDetectionEvents: riskDetection,
        NonInteractiveSignInEvents: nonInteractiveEvents,
        RiskyUserEvents: riskyUser,
        SecureScoreEvents: secureScore,
        SecurityAlertEvents: securityAlert,
        SecurityIncidentEvents: securityIncident,
        CaseAlertEvents: caseAlerts,
        IPAddresses: ips,
        Summary: summary,
      },
      serviceExcluded,
      safeExcluded,
      safeMarkedRowCount,
      postWindowCount,
      preWindowCount,
      riskDetectionAllEventsCount,
      preCompromiseCount,
      safeSessionCount,
      safeIpSessionCount,
      failedEventCount,
      dlpEventCount,
      unresolvedMailAccessCount,
      lookbackEventCount,
      totalCorrelationCount,
      dismissedEventCount,
    };
  }, [results, serviceIPSet, serviceIPs.length, effectiveSafeIPSet, effectiveSafeIPs.length, hideSafeIPs, hideEmailIPs, emailOnlyIPSet, attackerWindow, legAttribution, hidePostWindow, hidePreWindow, hideFailedEvents, hideDlpEvents, hideUnresolvedMailAccess, hideLookback, ipClassifications, dismissedEventIds, dismissedMode, safeMarkIds, urlClassifications, compromisedSessions, hidePreCompromise, safeSessionSet, hideSafeSessions, safeIpSessionSet, hideSafeIpSessions, nonInteractive, enrichmentOverlay, caseAlertRows, hideRiskDetections,
    // Phase 0 + 4.5c read the live flags: without this dep, flagging the lure does not
    // re-run the memo, so the release row keeps the generic wording and stays hidden
    // until something else invalidates it.
    phishingFlags]);

  // Per-type sub-tab dataset: the RAW results with ONLY the always-on baseline
  // applied (user-related security alerts/incidents). The toolbar toggles
  // (Safe IPs, Safe apps, MS substrate, Outlook noise, Post-window, Pre-compromise,
  // Failed, DLP, dismissed, sessions, lookback, …) are DELIBERATELY not applied here.
  // Those toggles scope to the "All Events" table only (which consumes
  // `filteredResults`); every dedicated sub-tab (Message Trace, Sign-In, SharePoint,
  // URL Clicks, DLP, General, Azure Activity, Risk, Security Alerts/Incidents) shows
  // its full record set. Reports, the excluded-tab badge counts, and the Attacker
  // Window panel continue to read `filteredResults` (unchanged).
  const baseResults = useMemo(() => {
    if (!results) return null;
    return {
      ...results,
      SecurityAlertEvents: (results.SecurityAlertEvents || []).filter(evt => evt.IsUserRelated),
      SecurityIncidentEvents: (results.SecurityIncidentEvents || []).filter(evt => evt.IsUserRelated),
    };
  }, [results]);

  // Which of this person's directory addresses the run searched, and whether the
  // analyst needs to be told that the search may have been narrower than it looks.
  const identityAliases = useMemo(
    () => aliasIdentifiers(results?.IdentityResolution),
    [results]
  );
  const identityBanner = useMemo(
    () => identityBannerState(results?.IdentityResolution),
    [results]
  );

  // Derive latest secure score from results
  const latestSecureScore = useMemo(() => {
    const scores = filteredResults.filtered?.SecureScoreEvents || [];
    if (scores.length === 0) return null;
    const sorted = [...scores].sort((a, b) =>
      (b.EventTimeUTC || '').localeCompare(a.EventTimeUTC || '')
    );
    return sorted[0];
  }, [filteredResults]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      // Same reason as the teardown inside startPolling: a poll already on the wire
      // must not come back and write state into a component that is gone.
      pollGenRef.current++;
      if (ipClassSaveTimerRef.current) clearTimeout(ipClassSaveTimerRef.current);
      if (sessionClassSaveTimerRef.current) clearTimeout(sessionClassSaveTimerRef.current);
      if (dismissedSaveTimerRef.current) clearTimeout(dismissedSaveTimerRef.current);
      if (urlClassSaveTimerRef.current) clearTimeout(urlClassSaveTimerRef.current);
      if (phishingFlagsSaveTimerRef.current) clearTimeout(phishingFlagsSaveTimerRef.current);
      if (ipCommentSaveTimerRef.current) clearTimeout(ipCommentSaveTimerRef.current);
    };
  }, []);

  // Start polling for a given jobId (used by both trigger and resume)
  //
  // Three guards, all of them earned on 2026-08-18 (v1.146.1). A single
  // GetRunbookStatus invocation hung for 135 s inside Set-AzContext and then threw
  // "The operation was canceled."; it resolved 2.2 s AFTER a later poll had already
  // reported Completed, loaded the results and torn the loop down. The old catch
  // wrote runbookError unconditionally, so the analyst saw a red "Investigation
  // failed" beside a green "completed successfully" on a run that produced an
  // 11.5 MB results blob. The work was never in danger — it runs in Azure
  // Automation — the portal simply mis-narrated it.
  //
  //   1. GENERATION — every start and every teardown bumps pollGenRef, and a
  //      response whose generation is stale writes nothing. A terminal state can
  //      no longer be overwritten by something that was already in flight.
  //   2. IN FLIGHT — skip a tick while a request is outstanding, so one slow poll
  //      cannot stack 45 more behind it.
  //   3. ERROR STREAK — a failed poll is NOT evidence of a failed investigation, so
  //      tolerate a run of them before saying anything, and then say the right
  //      thing (see runbookOutcomeUnknown).
  const startPolling = useCallback((jobId) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    const gen = ++pollGenRef.current;
    pollInFlightRef.current = false;
    pollFailStreakRef.current = 0;

    setRunbookRunning(true);
    setRunbookStatus({ status: 'Running', message: 'Investigation in progress...' });
    setRunbookError(null);
    setRunbookOutcomeUnknown(false);

    // Ends this cycle. The generation bump is the load-bearing part: it invalidates
    // any response still on the wire.
    const stopPolling = () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      pollGenRef.current++;
      setRunbookRunning(false);
    };

    pollTimerRef.current = setInterval(async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const status = await pollRunbookStatus(caseId, jobId);
        if (gen !== pollGenRef.current) return; // superseded — this answer is stale
        pollFailStreakRef.current = 0;
        setRunbookStatus(status);

        if (status.status === 'Completed') {
          stopPolling();
          await loadResults();
          await loadCase();
        } else if (status.status === 'Failed' || status.status === 'Stopped' || status.status === 'Suspended') {
          stopPolling();
          // A real terminal failure reported by Automation — this one IS the run.
          setRunbookOutcomeUnknown(false);
          setRunbookError(status.message || 'Investigation failed');
        }
      } catch (err) {
        if (gen !== pollGenRef.current) return;

        // Keep polling through a transient failure. The interval is still alive, so
        // the next tick may well answer; only give up after a sustained run.
        pollFailStreakRef.current += 1;
        if (pollFailStreakRef.current < POLL_MAX_CONSECUTIVE_ERRORS) return;

        stopPolling();
        setRunbookOutcomeUnknown(true);
        if (err?.isConnectionError) {
          // Lost the connection while polling — the investigation is almost
          // certainly still running server-side; the poll just couldn't reach us.
          // Don't imply it failed or that a re-run is needed.
          setRunbookError(
            "Lost connection while checking status — the investigation is most likely still running. Reload the page shortly to see the results; you don't need to start it over."
          );
        } else {
          const detail = err?.message ? ` (${err.message})` : '';
          setRunbookError(
            `Couldn't read the investigation's status ${POLL_MAX_CONSECUTIVE_ERRORS} times in a row${detail}. ` +
            'This says nothing about the investigation itself — it runs in Azure Automation, not in the portal, ' +
            "so it is most likely still running or already finished. Reload the page shortly to see the results; you don't need to start it over."
          );
        }
      } finally {
        pollInFlightRef.current = false;
      }
    }, POLL_INTERVAL_MS);
  }, [caseId, loadResults, loadCase]);

  // Resume polling if case has an active run (e.g., user navigated away and back)
  const hasResumedRef = useRef(false);
  useEffect(() => {
    if (hasResumedRef.current || !caseData) return;
    const activeStatuses = ['Running', 'Queued', 'New', 'Activating', 'Starting'];
    if (caseData.lastRunId && activeStatuses.includes(caseData.lastRunStatus)) {
      hasResumedRef.current = true;
      startPolling(caseData.lastRunId);
    }
  }, [caseData, startPolling]);

  // Cycle IP status: default → safe → suspect → malicious → default
  const handleCycleIPStatus = useCallback((ip) => {
    setIPClassifications((prev) => {
      const currentStatus = prev[ip] || (tenantSafeIPs.includes(ip) ? 'safe' : 'default');
      const nextStatus = getNextIPStatus(currentStatus);
      const updated = { ...prev };
      if (nextStatus === 'default' && !tenantSafeIPs.includes(ip)) {
        delete updated[ip]; // no need to store default for non-tenant IPs
      } else {
        updated[ip] = nextStatus;
      }
      ipClassificationsRef.current = updated;
      return updated;
    });

    // Debounced auto-save to backend
    if (ipClassSaveTimerRef.current) {
      clearTimeout(ipClassSaveTimerRef.current);
    }
    ipClassSaveTimerRef.current = setTimeout(async () => {
      setIPClassSaving(true);
      try {
        await updateCase(caseId, {
          ipClassifications: JSON.stringify(ipClassificationsRef.current),
        });
      } catch (err) {
        console.error('[CaseDetail] Failed to save IP classifications:', err);
      } finally {
        setIPClassSaving(false);
      }
    }, 1000);
  }, [caseId, tenantSafeIPs]);

  // Set an IP to a SPECIFIC classification (default/safe/suspect/malicious) — used by
  // the explicit segmented selector in the All Events expanded row (vs the cycling
  // handleCycleIPStatus the IP table / Devices tab use). Same debounced auto-save.
  const handleSetIPStatus = useCallback((ip, status) => {
    setIPClassifications((prev) => {
      const updated = { ...prev };
      if (status === 'default' && !tenantSafeIPs.includes(ip)) {
        delete updated[ip]; // no need to store default for non-tenant IPs
      } else {
        updated[ip] = status;
      }
      ipClassificationsRef.current = updated;
      return updated;
    });

    if (ipClassSaveTimerRef.current) {
      clearTimeout(ipClassSaveTimerRef.current);
    }
    ipClassSaveTimerRef.current = setTimeout(async () => {
      setIPClassSaving(true);
      try {
        await updateCase(caseId, {
          ipClassifications: JSON.stringify(ipClassificationsRef.current),
        });
      } catch (err) {
        console.error('[CaseDetail] Failed to save IP classifications:', err);
      } finally {
        setIPClassSaving(false);
      }
    }, 1000);
  }, [caseId, tenantSafeIPs]);

  // Add / edit / remove a freeform comment on a single IP. Edited only from the IP
  // Addresses tab; an empty string removes the entry. Debounced 1s auto-save to the
  // case entity, mirroring handleCycleIPStatus. Comment capped at 200 chars (ATS 64KB
  // per-property limit — see memory feedback_table_storage_64kb_property_limit).
  const handleSetIPComment = useCallback((ip, comment) => {
    setIPComments((prev) => {
      const updated = { ...prev };
      const trimmed = (comment || '').trim().slice(0, 200);
      if (trimmed) {
        updated[ip] = trimmed;
      } else {
        delete updated[ip];
      }
      ipCommentsRef.current = updated;
      return updated;
    });

    if (ipCommentSaveTimerRef.current) {
      clearTimeout(ipCommentSaveTimerRef.current);
    }
    ipCommentSaveTimerRef.current = setTimeout(async () => {
      try {
        await updateCase(caseId, {
          ipComments: JSON.stringify(ipCommentsRef.current),
        });
      } catch (err) {
        console.error('[CaseDetail] Failed to save IP comments:', err);
      }
    }, 1000);
  }, [caseId]);

  // Debounced persistence of the enrichment overlay + real-user evidence onto the
  // case entity (v1.98.2), so both survive a page reload (the investigation results
  // blob isn't rewritten by an enrichment refresh / evidence grade). Always sends the
  // full current maps; updateCase merges the fields.
  const saveOverlaysDebounced = useCallback(() => {
    if (overlaySaveTimerRef.current) clearTimeout(overlaySaveTimerRef.current);
    overlaySaveTimerRef.current = setTimeout(async () => {
      try {
        await updateCase(caseId, {
          ipEnrichmentOverlay: JSON.stringify(enrichmentOverlayRef.current || {}),
          ipEvidence: JSON.stringify(evidenceRef.current || {}),
        });
      } catch (err) {
        console.error('[CaseDetail] Failed to persist case overlays:', err);
      }
    }, 1200);
  }, [caseId]);

  // On-demand IP enrichment refresh (admin-only) — synchronous call to
  // /api/manage/ip-enrichment/refresh. ProxyCheck.io is queried and the result
  // is MERGEd into silver_m365_unified_ip_enrichment. The fresh values are
  // overlaid onto the in-memory results.IPAddresses row AND recorded in the
  // persisted overlay (saved to the case entity) so they survive a reload.
  // Returns the fresh payload on success; throws (with .status/.body) so the
  // caller can drive the 409 force-confirm and error display.
  const handleRefreshIpEnrichment = useCallback(async (ip, force = false) => {
    if (!ip) return null;
    let fresh;
    try {
      fresh = await refreshIpEnrichment(ip, { force });
    } catch (e) {
      // A 409 means the IP was enriched <24h ago; the API now returns that existing
      // enrichment in e.body.enrichment. Overlay it directly — the row populates with
      // the real data and no ProxyCheck query is burned. Re-throw anything else (and a
      // 409 with no body, defensively) so the IP table can surface the error / prompt.
      if (e?.status === 409 && e?.body?.enrichment) {
        fresh = e.body.enrichment;
      } else {
        throw e;
      }
    }
    setResults((prev) => {
      if (!prev) return prev;
      const ips = (prev.IPAddresses || []).map((row) =>
        row.IPAddress === ip ? applyEnrichmentToRow(row, fresh) : row
      );
      return { ...prev, IPAddresses: ips };
    });
    const mergedOne = { ...(enrichmentOverlayRef.current || {}), [String(ip).toLowerCase()]: fresh };
    enrichmentOverlayRef.current = mergedOne;
    setEnrichmentOverlay(mergedOne);
    saveOverlaysDebounced();
    return fresh;
  }, [saveOverlaysDebounced]);

  // Batch enrichment for all unenriched IPs in the table. Calls the batch
  // endpoint (ProxyCheck in <=100 chunks + surfaces existing Delta rows) and
  // overlays every returned row onto results.IPAddresses in one pass. Returns
  // the response envelope so the caller can report counts/errors.
  const handleRefreshIpEnrichmentBatch = useCallback(async (ips, force = false) => {
    const list = (ips || []).filter(Boolean);
    if (list.length === 0) return null;
    const resp = await refreshIpEnrichmentBatch(list, { force });
    const freshByIp = new Map(
      (resp?.results || []).map((r) => [String(r.ipAddress || '').toLowerCase(), r])
    );
    if (freshByIp.size > 0) {
      setResults((prev) => {
        if (!prev) return prev;
        const ipsOut = (prev.IPAddresses || []).map((row) => {
          const fresh = freshByIp.get(String(row.IPAddress || '').toLowerCase());
          return fresh ? applyEnrichmentToRow(row, fresh) : row;
        });
        return { ...prev, IPAddresses: ipsOut };
      });
      const merged = { ...(enrichmentOverlayRef.current || {}) };
      freshByIp.forEach((fresh, ipLower) => { merged[ipLower] = fresh; });
      enrichmentOverlayRef.current = merged;
      setEnrichmentOverlay(merged);
      saveOverlaysDebounced();
    }
    return resp;
  }, [saveOverlaysDebounced]);

  // Real-user evidence: grade each IP (tier 1 managed / 2 registered-BYOD / 0 none)
  // from the user's own sign-in device binding + the Entra device registry, to help
  // the analyst clear benign IPs with evidence. Returns the response envelope; the
  // IP table owns the per-IP evidence state for rendering (so it survives filters).
  const handleFetchRealUserEvidence = useCallback(async (ips) => {
    const list = (ips || []).filter(Boolean);
    if (list.length === 0 || !caseData) return null;
    // Match on the case's full identity set: identifiers (email-only) UNION its
    // entraObjectId — the sign-in history binds device evidence under the raw object
    // GUID on Teams / app token rows, which a single-UPN filter silently ignores.
    // Same derivation as handleFetchNonInteractive. userId (PrimaryUpn) is kept as a
    // representative and folds into the union server-side.
    const identity = results?.IdentityResolution || null;
    const identifiers = [];
    (identity?.Identifiers || []).forEach((id) => { if (id) identifiers.push(id); });
    if (identifiers.length === 0 && caseData.userPrincipalName) {
      identifiers.push(String(caseData.userPrincipalName).toLowerCase());
    }
    return fetchCaseIpEvidence(caseId, {
      tenantId: caseData.tenantId,
      // This endpoint keys on sign-in history, which is recorded under the Entra UPN.
      // A case opened under the user's email address would find nothing without this.
      userId:        identity?.PrimaryUpn || caseData.userPrincipalName,
      identifiers,
      entraObjectId: identity?.EntraObjectId || '',
      ips:           list,
      days:          30,
    });
  }, [caseId, caseData, results]);

  // Persist the non-interactive aggregate onto the case entity so the recovered IPs —
  // and the evidence that justifies classifying them — survive a reload. Same shape as
  // the ipEnrichmentOverlay / ipEvidence pattern; a new persistable case field needs
  // UpdateCase's allowlist AND GetCase AND normalizeCase or it is silently dropped.
  //
  // The reduction itself lives in `utils/nonInteractivePersist.js` (v1.148.9). It is
  // pure size arithmetic against the Azure Table Storage per-property cap, it has now
  // shipped two silent defects, and inline in a component it was untestable; it is
  // covered by `frontend/scripts/nonInteractivePersist.test.mjs`.
  const saveNonInteractiveDebounced = useCallback((payload) => {
    if (nonInteractiveSaveTimerRef.current) clearTimeout(nonInteractiveSaveTimerRef.current);
    nonInteractiveSaveTimerRef.current = setTimeout(async () => {
      try {
        const { serialized, persist, oversizeChars } = buildNonInteractivePersist(payload);

        if (serialized === null) {
          // Nothing left to shed. Do not attempt the write - it would 400 and take the
          // whole entity update with it. Tell the analyst; never fail silently.
          console.warn('[CaseDetail] Non-interactive aggregate still %d chars after full reduction; not persisted.', oversizeChars);
          setNonInteractive((prev) => (prev ? { ...prev, persist: { reduced: true, failed: true, dropped: 'everything', note: 'Too large to save on this case — it stays until you reload.' } } : prev));
          return;
        }

        await updateCase(caseId, { nonInteractiveIps: serialized });
        if (persist) {
          setNonInteractive((prev) => (prev ? { ...prev, persist } : prev));
        }
      } catch (err) {
        console.error('[CaseDetail] Failed to persist non-interactive aggregate:', err);
        setNonInteractive((prev) => (prev ? { ...prev, persist: { reduced: true, failed: true, note: 'Could not be saved — it stays until you reload.' } } : prev));
      }
    }, 1200);
  }, [caseId]);

  // Non-interactive sign-in activity (v1.147.0) — the on-demand aggregate that closes
  // the gap CaseScopeNotice discloses. AGGREGATE ONLY: distinct IPs and devices with
  // counts and time bounds, never events (59 rows from 19,037 on the reference case).
  //
  // The identifier set is `identityResolution.identifiers` UNION its `entraObjectId`.
  // That union is the point: `identifiers` is EMAIL-ONLY (the runbook builds it from the
  // typed address + resolved UPN + mail attribute), and the object id is a SIBLING field,
  // so filtering on the UPN alone silently drops Teams token traffic recorded under the
  // raw GUID. PostTokenUse and PostCaseIpEvidence were retrofitted to the same union in
  // v1.179.4 (handleFetchRealUserEvidence below derives it identically).
  const handleFetchNonInteractive = useCallback(async () => {
    if (!caseData) return null;
    const identity = results?.IdentityResolution || null;
    const identifiers = [];
    (identity?.Identifiers || []).forEach((id) => { if (id) identifiers.push(id); });
    // Fall back to what the case was opened under — never to an empty set, which the
    // API rejects. Resolution failure already falls back to the typed address upstream.
    if (identifiers.length === 0 && caseData.userPrincipalName) {
      identifiers.push(String(caseData.userPrincipalName).toLowerCase());
    }
    const resp = await fetchCaseNonInteractiveIps(caseId, {
      tenantId:      caseData.tenantId,
      identifiers,
      entraObjectId: identity?.EntraObjectId || '',
      startDate:     (caseData.startDate || '').substring(0, 10),
      endDate:       (caseData.endDate || '').substring(0, 10),
    });
    nonInteractiveRef.current = resp || null;
    setNonInteractive(resp || null);
    saveNonInteractiveDebounced(resp);
    return resp;
  }, [caseId, caseData, results, saveNonInteractiveDebounced]);

  // The IP table owns the live evidence map; mirror it here + persist (debounced) so
  // graded tiers survive a reload. Also push it into persistedEvidence so leaving and
  // returning to the IPs tab (which unmounts the table) re-seeds the pills without a reload.
  const handleEvidenceChange = useCallback((map) => {
    const m = map || {};
    evidenceRef.current = m;
    setPersistedEvidence(m);
    saveOverlaysDebounced();
  }, [saveOverlaysDebounced]);

  // Reset persisted-overlay hydration when navigating between cases (react-router reuses
  // this component instance across :caseId changes, so refs must be cleared explicitly).
  useEffect(() => {
    overlayHydratedRef.current = false;
    enrichmentOverlayRef.current = {};
    setEnrichmentOverlay({});
    evidenceRef.current = {};
    setPersistedEvidence(null);
    nonInteractiveRef.current = null;
    setNonInteractive(null);
  }, [caseId]);

  // Hydrate persisted enrichment overlay + evidence once results AND caseData are loaded.
  useEffect(() => {
    if (overlayHydratedRef.current) return;
    if (!results || !caseData) return;
    let ov = null;
    try { ov = caseData.ipEnrichmentOverlay ? JSON.parse(caseData.ipEnrichmentOverlay) : null; } catch { ov = null; }
    if (ov && Object.keys(ov).length) {
      enrichmentOverlayRef.current = ov;
      setEnrichmentOverlay(ov);
      setResults((prev) => {
        if (!prev) return prev;
        const ips = (prev.IPAddresses || []).map((row) => {
          const fresh = ov[String(row.IPAddress || '').toLowerCase()];
          return fresh ? applyEnrichmentToRow(row, fresh) : row;
        });
        return { ...prev, IPAddresses: ips };
      });
    }
    let ev = null;
    try { ev = caseData.ipEvidence ? JSON.parse(caseData.ipEvidence) : null; } catch { ev = null; }
    if (ev && Object.keys(ev).length) {
      evidenceRef.current = ev;
      setPersistedEvidence(ev);
    }
    // Non-interactive aggregate (v1.147.0) — rides the same one-shot so the recovered
    // IPs are back in the table on load without re-running the Databricks query.
    let ni = null;
    try { ni = caseData.nonInteractiveIps ? JSON.parse(caseData.nonInteractiveIps) : null; } catch { ni = null; }
    if (ni && Array.isArray(ni.ips) && ni.ips.length > 0) {
      nonInteractiveRef.current = ni;
      setNonInteractive(ni);
    }
    overlayHydratedRef.current = true;
  }, [results, caseData]);

  // Directly set one or more IPs to "safe" (the evidence panel's confirm action +
  // the bulk "Mark all real-user IPs Safe"). Unlike handleCycleIPStatus this jumps
  // straight to safe; one debounced save covers the whole batch.
  const handleSetIPsSafe = useCallback((ips) => {
    const list = (Array.isArray(ips) ? ips : [ips]).filter(Boolean);
    if (list.length === 0) return;
    setIPClassifications((prev) => {
      const updated = { ...prev };
      list.forEach((ip) => { updated[ip] = 'safe'; });
      ipClassificationsRef.current = updated;
      return updated;
    });
    if (ipClassSaveTimerRef.current) clearTimeout(ipClassSaveTimerRef.current);
    ipClassSaveTimerRef.current = setTimeout(async () => {
      setIPClassSaving(true);
      try {
        await updateCase(caseId, { ipClassifications: JSON.stringify(ipClassificationsRef.current) });
      } catch (err) {
        console.error('[CaseDetail] Failed to save IP classification:', err);
      } finally {
        setIPClassSaving(false);
      }
    }, 1000);
  }, [caseId]);
  const handleSetIPSafe = useCallback((ip) => handleSetIPsSafe([ip]), [handleSetIPsSafe]);

  // Bulk-promote the case's malicious-classified IPs into the cross-tenant Known
  // Malicious IPs blocklist (type='attacker', notes='per case {caseId}'). Global-admin
  // only (server-enforced). Returns the API summary so the confirm modal can report it.
  const handleAddMaliciousIPsToKnownList = useCallback(
    (ips) => addKnownMaliciousIPsFromCase(caseData?.caseId, ips),
    [caseData?.caseId]
  );

  // Cycle session-id status: default ↔ safe (2-state in v1).
  // The "compromised wins" rule is enforced at filter time via `safeSessionSet`,
  // not here — we still let the analyst record the classification even on a
  // compromised session in case they later un-classify the malicious IP.
  const handleCycleSessionStatus = useCallback((sid) => {
    if (!sid) return;
    // An attacker mark is removed through its own control (it carries a reason and was
    // stamped by the server); cycling must never overwrite it with 'safe'.
    if (sessionStatusOf(sessionClassificationsRef.current, sid) === 'attacker') return;
    setSessionClassifications((prev) => {
      const current = sessionStatusOf(prev, sid);
      const updated = { ...prev };
      if (current === 'safe') {
        delete updated[sid];
      } else {
        updated[sid] = 'safe';
      }
      sessionClassificationsRef.current = updated;
      return updated;
    });

    // Debounced auto-save (mirrors handleCycleIPStatus).
    if (sessionClassSaveTimerRef.current) clearTimeout(sessionClassSaveTimerRef.current);
    sessionClassSaveTimerRef.current = setTimeout(async () => {
      try {
        await updateCase(caseId, {
          sessionClassifications: JSON.stringify(sessionClassificationsRef.current),
        });
      } catch (err) {
        console.error('[CaseDetail] Failed to save session classifications:', err);
      }
    }, 1000);
  }, [caseId]);

  // Mark a session as the attacker's, or remove that mark (v1.190.0; utils/sessionMarks.js).
  // reason: a non-empty string sets the mark; null removes it. Saved immediately rather than
  // debounced, and the server's copy is adopted, because `by`/`at` are stamped there. A
  // pending debounced Safe save is folded in (the whole map is sent). On a failed save the
  // previous classifications are restored and the error rethrown so the control can say so.
  const handleSetSessionAttackerMark = useCallback(async (sid, reason) => {
    if (!sid) return;
    if (sessionClassSaveTimerRef.current) { clearTimeout(sessionClassSaveTimerRef.current); sessionClassSaveTimerRef.current = null; }
    const prev = sessionClassificationsRef.current;
    const next = { ...prev };
    const text = reason == null ? '' : String(reason).trim();
    if (!text) delete next[sid];
    else {
      const old = prev[sid] && typeof prev[sid] === 'object' ? prev[sid] : {};
      next[sid] = { verdict: 'attacker', reason: text, by: old.by || '', at: old.at || '' };
    }
    sessionClassificationsRef.current = next;
    setSessionClassifications(next);
    try {
      const updated = await updateCase(caseId, { sessionClassifications: JSON.stringify(next) });
      const saved = parseSessionClassifications(updated?.sessionClassifications);
      sessionClassificationsRef.current = saved;
      setSessionClassifications(saved);
    } catch (err) {
      sessionClassificationsRef.current = prev;
      setSessionClassifications(prev);
      throw err;
    }
  }, [caseId]);
  const sessionMarkCtx = useMemo(() => ({ onSet: handleSetSessionAttackerMark }), [handleSetSessionAttackerMark]);

  // Toggle dismissed status for an event (add/remove from dismissed set)
  // Accepts one event id or SEVERAL. A collapsed message-trace row stands for every
  // recipient row of one message, and those must move together: dismissing "the message"
  // while one recipient row stayed visible would leave a row whose recipient list is a
  // silent subset of the real one. Toggle direction is decided for the whole group --
  // not all dismissed => dismiss all -- so it is idempotent whatever state they were in.
  const handleDismissEvent = useCallback((eventIdOrIds) => {
    const ids = (Array.isArray(eventIdOrIds) ? eventIdOrIds : [eventIdOrIds]).filter(Boolean);
    if (ids.length === 0) return;
    setDismissedEventIds((prev) => {
      const updated = new Set(prev);
      const allDismissed = ids.every((id) => updated.has(id));
      for (const id of ids) {
        if (allDismissed) updated.delete(id);
        else updated.add(id);
      }
      dismissedIdsRef.current = updated;
      return updated;
    });

    // Debounced auto-save to backend
    if (dismissedSaveTimerRef.current) {
      clearTimeout(dismissedSaveTimerRef.current);
    }
    dismissedSaveTimerRef.current = setTimeout(async () => {
      try {
        await updateCase(caseId, {
          dismissedEventIds: JSON.stringify([...dismissedIdsRef.current]),
        });
      } catch (err) {
        console.error('[CaseDetail] Failed to save dismissed events:', err);
      }
    }, 1000);
  }, [caseId]);

  // Mark (reason = text) or unmark (reason = null) rows as ATTACKER ACTIVITY (v1.189.0).
  // Accepts one id or several — a collapsed row stands for every constituent, like dismiss.
  // Optimistic, then replaced by the SERVER's map (which carries the stamped by/at); on a
  // failed save the previous marks are restored and the error is rethrown so the control can
  // say so — a mark the analyst believes is saved but is not would silently drop out of the
  // report, which reads the Cases entity.
  // value: null removes the row's mark; a string is an ATTACKER mark with that reason;
  // { verdict: 'safe' } is a SAFE mark (v1.189.8) -- the row is benign, its address untouched.
  // One map, so a row is never both.
  const handleSetAttackerMark = useCallback(async (eventIdOrIds, value) => {
    const ids = (Array.isArray(eventIdOrIds) ? eventIdOrIds : [eventIdOrIds]).filter(Boolean);
    if (ids.length === 0) return;
    const prev = attackerMarksRef.current;
    const next = new Map(prev);
    const safe = value && typeof value === 'object' && value.verdict === 'safe';
    const text = safe ? String(value.reason || '').trim() : (value == null ? null : String(value).trim());
    for (const id of ids) {
      const keep = { by: prev.get(id)?.by || '', at: prev.get(id)?.at || '' };
      if (safe) next.set(id, { verdict: 'safe', reason: text, ...keep });
      else if (!text) next.delete(id);
      else next.set(id, { reason: text, ...keep });
    }
    attackerMarksRef.current = next;
    setAttackerEventMarks(next);
    try {
      const updated = await updateCase(caseId, {
        attackerEventIds: JSON.stringify(Object.fromEntries(next)),
      });
      const saved = parseAttackerEventMarks(updated?.attackerEventIds);
      attackerMarksRef.current = saved;
      setAttackerEventMarks(saved);
    } catch (err) {
      attackerMarksRef.current = prev;
      setAttackerEventMarks(prev);
      throw err;
    }
  }, [caseId]);

  // Cycle URL click classification: default → confirmed → suspected → benign → default
  // When cycling TO confirmed: auto-flag matching message trace event as phishing
  // When cycling FROM confirmed: auto-remove the phishing flag
  const handleCycleUrlStatus = useCallback((eventId) => {
    setUrlClassifications((prev) => {
      const currentStatus = prev[eventId] || 'default';
      const nextStatus = getNextUrlStatus(currentStatus);
      const updated = { ...prev };
      if (nextStatus === 'default') {
        delete updated[eventId];
      } else {
        updated[eventId] = nextStatus;
      }
      urlClassificationsRef.current = updated;

      // Auto-link phishing flag via NetworkMessageId
      const urlClickEvent = (results?.UrlClickEvents || []).find(e => e.EventId === eventId);
      if (urlClickEvent?.NetworkMessageId) {
        const msgTraceEvent = (results?.MessageTraceEvents || []).find(
          e => e.EventId === urlClickEvent.NetworkMessageId || e.NetworkMessageId === urlClickEvent.NetworkMessageId
        );
        if (msgTraceEvent) {
          const msgEventId = msgTraceEvent.EventId;
          if (nextStatus === 'confirmed' && currentStatus !== 'confirmed') {
            // Entering confirmed — add phishing flag if not already flagged
            setPhishingFlags((prevFlags) => {
              if (prevFlags.some(f => f.eventId === msgEventId)) return prevFlags;
              const updatedFlags = [...prevFlags, {
                eventId: msgEventId,
                senderAddress: msgTraceEvent.SenderAddress || '',
                subject: msgTraceEvent.Subject || '',
              }];
              phishingFlagsRef.current = updatedFlags;
              // Save phishing flags (debounced with URL classification save below)
              if (phishingFlagsSaveTimerRef.current) clearTimeout(phishingFlagsSaveTimerRef.current);
              phishingFlagsSaveTimerRef.current = setTimeout(async () => {
                try {
                  await updateCase(caseId, { phishingFlags: JSON.stringify(phishingFlagsRef.current) });
                } catch (err) {
                  console.error('[CaseDetail] Failed to auto-save phishing flags:', err);
                }
              }, 1000);
              return updatedFlags;
            });
          } else if (currentStatus === 'confirmed' && nextStatus !== 'confirmed') {
            // Leaving confirmed — only remove flag if no other confirmed clicks share this message
            const otherConfirmedExists = (results?.UrlClickEvents || []).some(
              e => e.EventId !== eventId
                && e.NetworkMessageId === urlClickEvent.NetworkMessageId
                && updated[e.EventId] === 'confirmed'
            );
            if (!otherConfirmedExists) {
              setPhishingFlags((prevFlags) => {
                const updatedFlags = prevFlags.filter(f => f.eventId !== msgEventId);
                phishingFlagsRef.current = updatedFlags;
                if (phishingFlagsSaveTimerRef.current) clearTimeout(phishingFlagsSaveTimerRef.current);
                phishingFlagsSaveTimerRef.current = setTimeout(async () => {
                  try {
                    await updateCase(caseId, { phishingFlags: JSON.stringify(phishingFlagsRef.current) });
                  } catch (err) {
                    console.error('[CaseDetail] Failed to auto-save phishing flags:', err);
                  }
                }, 1000);
                return updatedFlags;
              });
            }
          }
        }
      }

      return updated;
    });

    // Debounced auto-save to backend
    if (urlClassSaveTimerRef.current) {
      clearTimeout(urlClassSaveTimerRef.current);
    }
    urlClassSaveTimerRef.current = setTimeout(async () => {
      try {
        await updateCase(caseId, {
          urlClassifications: JSON.stringify(urlClassificationsRef.current),
        });
      } catch (err) {
        console.error('[CaseDetail] Failed to save URL classifications:', err);
      }
    }, 1000);
  }, [caseId, results]);

  // Toggle phishing flag on a message trace event
  const handleTogglePhishingFlag = useCallback((eventId, senderAddress, subject) => {
    setPhishingFlags((prev) => {
      const exists = prev.some(f => f.eventId === eventId);
      let updated;
      if (exists) {
        updated = prev.filter(f => f.eventId !== eventId);
      } else {
        updated = [...prev, { eventId, senderAddress, subject }];
      }
      phishingFlagsRef.current = updated;
      return updated;
    });

    // Debounced auto-save to backend
    if (phishingFlagsSaveTimerRef.current) {
      clearTimeout(phishingFlagsSaveTimerRef.current);
    }
    phishingFlagsSaveTimerRef.current = setTimeout(async () => {
      try {
        await updateCase(caseId, {
          phishingFlags: JSON.stringify(phishingFlagsRef.current),
        });
      } catch (err) {
        console.error('[CaseDetail] Failed to save phishing flags:', err);
      }
    }, 1000);
  }, [caseId]);

  // 🔴 GENERATE IS A PERSISTENCE BOUNDARY (v1.178.0).
  //
  // `handleTogglePhishingFlag` and the URL-classification handler save on a ONE-SECOND
  // DEBOUNCE and never call `setCaseData`. `RequestExecutiveReport` then re-reads the Cases
  // ENTITY and `Invoke-ExecReportReconcile` takes the authoritative flags ONLY from it,
  // discarding the client's copies — deliberately, because those judgments license the
  // report's strongest claims. The consequence is that flagging the lure and clicking
  // Generate inside that one second produces a report with the entry-point finding
  // MISSING, silently: the pack carried it, the server rebuilt from an entity that did
  // not, and nothing anywhere says so.
  //
  // So the flush is awaited before generation, and a failed save STOPS it. The refs are
  // the source of truth here rather than state, for the same reason the debounced timers
  // read them: they hold the latest value even mid-render.
  const flushCaseJudgments = useCallback(async () => {
    const pending = {};
    if (phishingFlagsSaveTimerRef.current) {
      clearTimeout(phishingFlagsSaveTimerRef.current);
      phishingFlagsSaveTimerRef.current = null;
      pending.phishingFlags = JSON.stringify(phishingFlagsRef.current || []);
    }
    if (urlClassSaveTimerRef.current) {
      clearTimeout(urlClassSaveTimerRef.current);
      urlClassSaveTimerRef.current = null;
      pending.urlClassifications = JSON.stringify(urlClassificationsRef.current || {});
    }
    if (Object.keys(pending).length === 0) return;
    try {
      await updateCase(caseId, pending);
    } catch (err) {
      // Rethrown with a code the modal branches on, so the analyst is told rather than
      // handed a report built from a stale entity.
      const wrapped = new Error(
        'Could not save this case’s findings before generating. The report would be '
        + 'built without them. Check your connection and try again.'
      );
      wrapped.body = { code: 'case_save_failed' };
      throw wrapped;
    }
  }, [caseId]);

  // Toggle known app flag (add/remove from tenant or global known apps)
  const handleToggleKnownApp = useCallback(async (appId, displayName, markAsKnown, scope = 'tenant') => {
    if (!caseData?.tenantId) return;
    setKnownAppSaving(true);
    try {
      if (markAsKnown) {
        await addKnownApp({
          appId,
          displayName: displayName || `Unknown (${appId})`,
          category: 'Custom',
          ...(scope === 'global' ? {} : { tenantId: caseData.tenantId }),
        });
      } else {
        // Remove from tenant scope (or global if that's where it lives)
        await removeKnownApp(appId, caseData.tenantId);
      }
      // Refresh known apps list
      const updated = await fetchKnownApps(caseData.tenantId);
      setKnownApps(Array.isArray(updated) ? updated : []);
    } catch (err) {
      console.error('[CaseDetail] Failed to toggle known app:', err);
    } finally {
      setKnownAppSaving(false);
    }
  }, [caseData?.tenantId]);

  // Run investigation
  const handleRunInvestigation = async () => {
    setRunbookRunning(true);
    setRunbookStatus(null);
    setRunbookError(null);
    setRunbookOutcomeUnknown(false);

    try {
      const params = {};
      if (startDate) params.StartDate = startDate;
      if (endDate) params.EndDate = endDate;

      const result = await triggerRunbook(caseId, params);
      startPolling(result.jobId);
    } catch (err) {
      setRunbookRunning(false);
      if (err?.isConnectionError) {
        // The trigger is a write: a dropped connection does NOT mean it didn't
        // land — the runbook often starts server-side and only the response is
        // lost. Steer to a reload-and-check so we don't spawn duplicate jobs.
        setRunbookOutcomeUnknown(true);
        setRunbookError(
          "Couldn't confirm the investigation started — the connection dropped before the server responded. It may already be running. Reload the page in a moment to check before starting it again."
        );
      } else {
        setRunbookError(err.message || 'Failed to start investigation');
      }
    }
  };

  // Toggle case status (Open <-> Closed)
  const handleToggleStatus = async () => {
    if (!caseData || statusUpdating) return;
    const newStatus = caseData.status === 'Closed' ? 'Open' : 'Closed';
    setStatusUpdating(true);
    setStatusError(null);
    try {
      const updated = await updateCase(caseId, { status: newStatus });
      setCaseData((prev) => ({ ...prev, ...updated }));
    } catch (err) {
      console.error('[CaseDetail] Failed to update case status:', err);
      setStatusError(err.message || 'Failed to update case status');
    } finally {
      setStatusUpdating(false);
    }
  };

  // Executive Report (AI). The fact-pack builder consumes the EXACT same params
  // object the two deterministic reports pass, so there is one definition of "what
  // this report is about" rather than a second, drifting one — plus the resolved
  // timezone (the pack pre-formats every time, so the model never does tz math),
  // the source run id (the server pins and reconciles against it), and the app
  // version for provenance.
  // All FIFTEEN filter-bar toggles as one object. The Executive Report requires a fixed
  // ("canonical") state and refuses to generate otherwise, and the SERVER verifies it —
  // so every state has to leave the browser, not just the three that used to.
  //
  // 🔴 EVERY key in CANONICAL_FILTERS must appear here. `filterProfileDiff` counts a
  // MISSING key as wrong (deliberately — a pack that omitted a toggle would otherwise
  // pass the gate), so an absent key is not a soft failure: the gate reports that filter
  // as unset, "Set the report view" sets the React state but the key still never reaches
  // the modal, the diff never clears, and the Executive Report becomes IMPOSSIBLE to
  // generate. That is exactly what shipped in v1.154.0, which added `hideRiskDetections`
  // to CANONICAL_FILTERS and to the setter below but not to this object.
  // `frontend/scripts/execReportFactPack.test.mjs` now asserts the two lists agree.
  const caseFilterState = useMemo(() => ({
    hideSafeIPs,
    hideRiskDetections,
    hideKnownApps,
    hideMsSubstrate,
    hideOutlookNoise,
    hidePostWindow,
    hidePreWindow,
    hidePreCompromise,
    hideSafeSessions,
    hideSafeIpSessions,
    hideFailedEvents,
    hideDlpEvents,
    hideUnresolvedMailAccess,
    hideLookback,
    hideEmailIPs,
    dismissedMode,
  }), [
    hideSafeIPs, hideRiskDetections, hideKnownApps, hideMsSubstrate, hideOutlookNoise,
    hidePostWindow, hidePreWindow, hidePreCompromise, hideSafeSessions, hideSafeIpSessions,
    hideFailedEvents, hideDlpEvents, hideUnresolvedMailAccess, hideLookback,
    hideEmailIPs, dismissedMode,
  ]);

  // Applies the canonical report view in ONE action. Nothing about the filter bar is
  // persisted, so requiring the analyst to set fourteen toggles from memory before every
  // report would be a checklist, not a control.
  const applyReportFilterView = useCallback(() => {
    const want = canonicalFilterState();
    setHideSafeIPs(want.hideSafeIPs);
    setHideRiskDetections(want.hideRiskDetections);
    setHideKnownApps(want.hideKnownApps);
    setHideMsSubstrate(want.hideMsSubstrate);
    setHideOutlookNoise(want.hideOutlookNoise);
    setHidePostWindow(want.hidePostWindow);
    setHidePreWindow(want.hidePreWindow);
    setHidePreCompromise(want.hidePreCompromise);
    setHideSafeSessions(want.hideSafeSessions);
    setHideSafeIpSessions(want.hideSafeIpSessions);
    setHideFailedEvents(want.hideFailedEvents);
    setHideDlpEvents(want.hideDlpEvents);
    setHideUnresolvedMailAccess(want.hideUnresolvedMailAccess);
    setHideLookback(want.hideLookback);
    setHideEmailIPs(want.hideEmailIPs);
    setDismissedMode(want.dismissedMode);
  }, []);
  // How many pills differ from the report view -- drives the button at the head of the
  // filter bar (the same action as the report modal's "Set the report view").
  const reportViewDiffCount = useMemo(() => filterProfileDiff(caseFilterState).length, [caseFilterState]);

  // 🔴 `eventReviewCounts` LIVED HERE AND WAS DELETED IN v1.171.0. It summed the fourteen
  // filtered workload arrays, which counts records no view of the case can display -- 167
  // "incident related" over a 43-row screen on IR-20260826-gwoodward-001. The report now
  // derives both figures itself, from the same stream every other number in it comes from
  // (utils/execReportFactPack.js). Do not reintroduce a second derivation here: two places
  // computing "how much was reviewed" is how the sentence and the table came to disagree.

  const execReportParams = useMemo(() => ({
    // 🔴 LIVE FLAGS MERGED OVER `caseData`. `caseData.phishingFlags` is loaded once and
    // never re-set by the toggle handler, so within a session it is STALE — a flag added
    // (or removed) after the case loaded would not be in the pack at all. The flush above
    // makes the SERVER current; this makes the PACK current, and both are needed because
    // the server verifies the pack against the entity.
    //
    // Do not read `caseData.phishingFlags` anywhere new; read this.
    caseData: { ...caseData, phishingFlags },
    filteredResults,
    attackerWindow,
    ipClassifications,
    ipComments,
    urlClassifications,
    selectedTz,
    knownApps,
    effectiveSafeIPSet,
    tenantSafeIPs,
    includeLogBreakdown,
    hideMsSubstrate,
    hideOutlookNoise,
    maskSubjects: !revealSubjects,
    results,
    dismissedEventIds,
    attackerEventIds: attackerEventMarks,
    sessionMarks: sessionMarkMap,
    filterState: caseFilterState,
    tzIana: resolveIana((TIMEZONE_OPTIONS.find(t => t.id === selectedTz) || TIMEZONE_OPTIONS[0]).iana),
    tzLabel: getTzLabel(selectedTz),
    runId: caseData?.lastRunId || '',
    appVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '',
  }), [
    caseData, filteredResults, attackerWindow, ipClassifications, ipComments, urlClassifications,
    selectedTz, knownApps, effectiveSafeIPSet, tenantSafeIPs, includeLogBreakdown,
    hideMsSubstrate, hideOutlookNoise, revealSubjects, results, dismissedEventIds,
    caseFilterState, phishingFlags, attackerEventMarks, sessionMarkMap,
  ]);

  // 🔴 WITHHELD BY OPERATOR DECISION (2026-08-26), not a defect and not a permission gate.
// The Technical Report has not had the content review the Executive Brief has been
// through, and must not reach a client until it is revisited. The Executive Report was
// withheld on the same basis and restored in v1.204.6, after v1.204.0-.5 brought it into
// agreement with the Brief.
//
// DISABLED WITH THE REASON SHOWN, never hidden. An absent button reads as a missing
// feature and sends someone looking for it; a live button that does nothing reads as a
// bug (memory feedback_ui_open_api_gated_silent_button). Stating the reason is the only
// option that tells the truth about why it will not run.
//
// The handlers, the fact pack and the renderers are untouched -- flip a flag to false to
// restore the button, and nothing else has to change.
const WITHHELD_REPORTS = { executiveReport: false, technicalReport: true };
const WITHHELD_TITLE = 'Withheld pending review \u2014 this report is not available for use yet.';

// Generate Client Report (executive-friendly)
  const handleClientReport = () => {
    if (!attackerWindow || !results) return;
    // This report is rendered HERE in the browser, from live state, so it does not need
    // the entity write that Generate awaits. It calls the same flush anyway so a debounced
    // judgment is not left unsaved by an analyst who only ever produces this report —
    // fire-and-forget, because a failed save cannot corrupt a document built from state.
    flushCaseJudgments().catch((err) => {
      console.error('[CaseDetail] Could not flush case findings before the client report:', err);
    });
    openClientReport({
      // The Brief's own params, so its checklist is built from the same inputs.
      ...execReportParams,
      caseData,
      // 🔴 LIVE state, not `caseData.phishingFlags` (stale within a session).
      phishingFlags,
      compromiseScope,
    });
  };

  // Generate Technical Report (analyst working document)
  const handleTechnicalReport = () => {
    if (!attackerWindow || !results) return;
    openTechnicalReport({
      caseData,
      compromiseScope,
      filteredResults,
      attackerWindow,
      ipClassifications,
      ipComments,
      urlClassifications,
      selectedTz,
      knownApps,
      effectiveSafeIPSet,
      tenantSafeIPs,
      includeLogBreakdown,
      hideMsSubstrate,
      hideOutlookNoise,
      maskSubjects: !revealSubjects,
      results,
    });
  };

  // Per-case sensitive-detail reveal. One toggle unmasks every subject AND every
  // file / attachment name in the case (timeline, tabs, raw JSON, reports); turning
  // it ON logs a single audit entry (sensitive_detail.reveal / sensitive).
  // Ephemeral — re-masks on reload.
  //
  // The count MUST cover everything the toggle discloses, or the audit row
  // under-reports the disclosure. It used to count only Message Trace subjects and
  // Exchange Folders subjects, while the same toggle also revealed Exchange
  // ItemSubject, mail attachment names and SharePoint/OneDrive file names.
  const countCaseSubjects = () => {
    const r = results || {};
    let n = 0;
    for (const e of (r.MessageTraceEvents || [])) { if (e.Subject) n++; }
    for (const e of (r.ExchangeEvents || [])) {
      const f = e.Folders;
      if (typeof f === 'string' && f.includes('"Subject"')) n++;
      else if (f && Array.isArray(f.FolderItems) && f.FolderItems.some(i => i.Subject)) n++;
      if (e.ItemSubject) n++;
      if (e.ItemAttachments) {
        // Count the attachment NAMES disclosed, not the event. Falls back to 1 when
        // the list is unparseable, so an unparseable list still counts as a
        // disclosure rather than as nothing.
        const parsed = parseItemAttachments(e.ItemAttachments);
        n += parsed ? parsed.count : (e.ItemAttachmentCount != null ? e.ItemAttachmentCount : 1);
      }
    }
    for (const e of ([...(r.SharePointEvents || []), ...(r.ExchangeEvents || []), ...(r.GeneralEvents || [])])) {
      const f = e.Folders;
      if (typeof f === 'string') {
        if (f.includes('"FileName"')) n++;
      } else if (f) {
        for (const node of (Array.isArray(f) ? f : [f])) {
          if (node && (node.FileName || node.SourceFileName)) n++;
        }
      }
    }
    n += (phishingFlags || []).filter(p => p.subject).length;
    return n;
  };
  const handleToggleRevealSubjects = () => {
    setRevealSubjects((prev) => {
      const next = !prev;
      if (next) {
        // Fire-and-forget, fail-soft (logSubjectView swallows errors).
        logSubjectView({
          source: 'investigation',
          caseId,
          tenantId: caseData.tenantId,
          mailbox: caseData.userPrincipalName,
          subjectCount: countCaseSubjects(),
        });
      }
      return next;
    });
  };

  // Delete case (admin only)
  const handleDeleteCase = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete case ${caseId}? This will remove all associated data including investigation results and cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      await deleteCase(caseId);
      navigate('/cases');
    } catch (err) {
      console.error('[CaseDetail] Failed to delete case:', err);
      setStatusError(err.message || 'Failed to delete case');
      setDeleting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <svg
            className="animate-spin h-10 w-10 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="text-gray-500">Loading case...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !caseData) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/cases')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Dashboard
        </button>
        <div className="card text-center py-12">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-200 mb-2">
            Failed to Load Case
          </h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <button onClick={loadCase} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/cases')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Dashboard
      </button>

      {/* Case Header */}
      <div className="card mb-6">
        {/* Row 1: Case ID + Status Badge */}
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-xl font-bold text-gray-100 truncate">
            {caseData.caseId}
          </h1>
          <Badge tone={caseStatusTone(caseData.status)} className="flex-shrink-0">
            {caseData.status === 'InProgress' ? 'In Progress' : caseData.status}
          </Badge>
        </div>

        {/* Row 2: Metadata */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-400 mb-1">
          <span>
            <span className="text-gray-600">Tenant:</span>{' '}
            {caseData.tenantName || caseData.tenantId}
          </span>
          {caseData.tenantId && (
            <span className="group">
              <span className="text-gray-600">Tenant ID:</span>{' '}
              <span className="text-gray-200 font-mono">{caseData.tenantId}</span>
              <CopyButton text={caseData.tenantId} />
            </span>
          )}
          <span>
            <span className="text-gray-600">UPN:</span>{' '}
            <span className="text-gray-200 font-mono">{caseData.userPrincipalName}</span>
          </span>
          {/* This person's other directory addresses, resolved at run time. Exchange logs
              mailbox activity under the mail attribute, so without this an analyst has no
              way to tell whether those events were in scope. */}
          {identityAliases.length > 0 && (
            <span>
              <span className="text-gray-600">Also searched:</span>{' '}
              <span className="text-sky-300 font-mono">{identityAliases.join(', ')}</span>
            </span>
          )}
          {latestSecureScore && (
            <span>
              <span className="text-gray-600">Secure Score:</span>{' '}
              <span className={latestSecureScore.ScorePercentage >= 60 ? 'text-green-400' : latestSecureScore.ScorePercentage >= 40 ? 'text-amber-400' : 'text-red-400'}>
                {latestSecureScore.CurrentScore}/{latestSecureScore.MaxScore} ({Math.round(latestSecureScore.ScorePercentage)}%)
              </span>
            </span>
          )}
          {results?.UserBaseline?.snapshotDate && (() => {
            const bl = results.UserBaseline;
            const signIns = results?.SignInEvents || [];
            const capitalize = s => s ? s.replace(/\b\w/g, c => c.toUpperCase()) : '';
            const stateAbbr = s => {
              const map = { alabama:'AL',alaska:'AK',arizona:'AZ',arkansas:'AR',california:'CA',colorado:'CO',connecticut:'CT',delaware:'DE',florida:'FL',georgia:'GA',hawaii:'HI',idaho:'ID',illinois:'IL',indiana:'IN',iowa:'IA',kansas:'KS',kentucky:'KY',louisiana:'LA',maine:'ME',maryland:'MD',massachusetts:'MA',michigan:'MI',minnesota:'MN',mississippi:'MS',missouri:'MO',montana:'MT',nebraska:'NE',nevada:'NV','new hampshire':'NH','new jersey':'NJ','new mexico':'NM','new york':'NY','north carolina':'NC','north dakota':'ND',ohio:'OH',oklahoma:'OK',oregon:'OR',pennsylvania:'PA','rhode island':'RI','south carolina':'SC','south dakota':'SD',tennessee:'TN',texas:'TX',utah:'UT',vermont:'VT',virginia:'VA',washington:'WA','west virginia':'WV',wisconsin:'WI',wyoming:'WY' };
              return map[(s || '').toLowerCase()] || capitalize(s);
            };
            const countryAbbr = c => {
              const map = { 'united states':'USA','united kingdom':'UK',canada:'CA',australia:'AU',germany:'DE',france:'FR' };
              return map[(c || '').toLowerCase()] || capitalize(c);
            };
            // Resolve normalized OS to most common DeviceOS version from sign-in events
            const resolveOSVersion = (normalizedOS) => {
              const counts = {};
              signIns.forEach(evt => {
                const norm = evt.NormalizedOS || evt.DeviceOS || '';
                if (norm === normalizedOS && evt.DeviceOS) {
                  counts[evt.DeviceOS] = (counts[evt.DeviceOS] || 0) + 1;
                }
              });
              const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
              return best ? best[0] : normalizedOS;
            };
            // Convert UTC hour to selected timezone
            const tz = TIMEZONE_OPTIONS.find(t => t.id === selectedTz) || TIMEZONE_OPTIONS[0];
            const offsetStr = getTzOffset(tz.iana);
            const offsetHours = offsetStr ? parseFloat(offsetStr) : 0;
            const convertHour = (utcHour) => ((utcHour + offsetHours) % 24 + 24) % 24;
            const tzLabel = tz.label;
            const locationParts = [
              ...(bl.typicalCities || []).map(capitalize),
              ...(bl.typicalStates || []).map(stateAbbr),
              ...(bl.typicalCountries || []).map(countryAbbr),
            ].filter(Boolean);
            return (
              <span>
                <span className="text-gray-600">Baseline:</span>{' '}
                <span className="text-blue-400">
                  {bl.knownIPs?.length || 0} IPs
                  {bl.osProfiles?.length > 0 && (
                    <>, OS={bl.osProfiles.map(p => resolveOSVersion(p.os)).join('/')}, {bl.osProfiles.map(p => `${Math.floor(convertHour(p.earliestHour))}–${Math.floor(convertHour(p.latestHour))}h`).join(' / ')} ({tzLabel})</>
                  )}
                  {locationParts.length > 0 && (
                    <>, {locationParts.join(', ')}</>
                  )}
                </span>
                {' '}
                <span className="text-gray-600 text-xs">
                  (baseline data = {bl.windowStart} – {bl.windowEnd})
                </span>
              </span>
            );
          })()}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-400 mb-4">
          <span>
            <span className="text-gray-600">Created:</span>{' '}
            {formatDate(caseData.createdDate)}
          </span>
          {caseData.createdBy && (
            <span>
              <span className="text-gray-600">By:</span>{' '}
              <span className="font-mono">{caseData.createdBy}</span>
            </span>
          )}
          {caseData.ticketNumber && (
            <span>
              <span className="text-gray-600">Ticket:</span>{' '}
              {caseData.ticketNumber}
            </span>
          )}
        </div>

        {/* Row 3: Date range + actions (compact inline) */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="detail-start-date" className="text-xs text-gray-500 whitespace-nowrap">Start</label>
            <input
              id="detail-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field py-1.5 text-sm w-[150px]"
              disabled={runbookRunning}
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="detail-end-date" className="text-xs text-gray-500 whitespace-nowrap">End</label>
            <input
              id="detail-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
              className="input-field py-1.5 text-sm w-[150px]"
              disabled={runbookRunning}
            />
          </div>
          <button
            onClick={handleRunInvestigation}
            disabled={runbookRunning || caseData.status === 'Closed'}
            className="btn-primary flex items-center gap-2 whitespace-nowrap py-1.5 text-sm"
          >
            {runbookRunning ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <PlayIcon className="h-4 w-4" />
                Retrieve Logs
              </>
            )}
          </button>
          {caseData.lastRunDate ? (
            <span className="text-xs text-gray-500">
              Last run: {formatDate(caseData.lastRunDate)}
            </span>
          ) : (
            <span className="text-xs text-gray-600">Not yet run</span>
          )}
          <select
            value={selectedTz}
            onChange={handleTzChange}
            className="bg-gray-800 text-gray-300 text-xs border border-gray-700 rounded px-1.5 py-0.5 focus:outline-none focus:border-blue-600"
          >
            {TIMEZONE_OPTIONS.map(tz => (
              <option key={tz.id} value={tz.id}>{tz.label} ({getTzOffset(tz.iana)})</option>
            ))}
          </select>

          <div className="flex items-center gap-2 ml-auto">
            {/* 🔴 GLOBAL ADMIN ONLY, and hidden rather than disabled for everyone else: it is an
                attestation about how the investigation was conducted, not a view option, and a
                visibly-dead control invites someone to think the claim is already recorded. */}
            {isAdmin && (
              <label
                className={`flex items-center gap-1 text-xs ${ualSaving ? 'text-gray-600 cursor-wait' : 'text-gray-400 cursor-pointer'}`}
                title={'Records for this case were retrieved directly from Microsoft\'s Unified Audit Log, '
                  + 'outside the monitored log feeds.\n\nTick this when the period was examined that way. '
                  + 'Coverage is measured from what reached our warehouse, so an out-of-band retrieval '
                  + 'otherwise reads as a feed that started late, and the Executive Brief hedges a finding '
                  + 'you actually established. The report discloses that it is your attestation.'}
              >
                <input
                  type="checkbox"
                  checked={ualReviewed}
                  onChange={(e) => handleToggleUalReviewed(e.target.checked)}
                  disabled={ualSaving}
                  className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 h-3.5 w-3.5"
                />
                UAL reviewed
              </label>
            )}
            {/* 🔴 GLOBAL ADMIN ONLY, hidden for everyone else (like UAL reviewed). It licenses
                the reports to say "no attacker access was found", the most reassuring claim
                they make, so it is an attestation with computed preconditions — disabled with
                the unmet reasons shown until the gate is met. Always toggleable OFF once set,
                and a malicious IP (which fails the gate) forces it back to the default. */}
            {isAdmin && (() => {
              const scopeChecked = isNoAccessScope(compromiseScope);
              const scopeAllowed = scopeChecked || noAccessGate.eligible;
              const unmet = noAccessGate.checks.filter((c) => !c.met);
              const scopeTitle = scopeChecked
                ? 'Recorded: the credentials/session were captured but no attacker access to the account was found. '
                  + 'The Client Report and the Attacker Window read as a window of exposure, not confirmed access. Untick to revert.'
                : scopeAllowed
                  ? 'Record that the credentials/session were captured (a phishing-kit sign-in) but the reviewed activity shows no attacker '
                    + 'access to the account. The reports will say so as your determination, never "the attacker did not access the account".'
                  : 'Not available yet — this attestation requires:\n'
                    + unmet.map((c) => `• ${c.label}${c.detail ? ` — ${c.detail}` : ''}`).join('\n');
              return (
                <label
                  className={`flex items-center gap-1 text-xs ${scopeSaving ? 'text-gray-600 cursor-wait' : (scopeAllowed ? 'text-gray-400 cursor-pointer' : 'text-gray-600 cursor-not-allowed')}`}
                  title={scopeTitle}
                >
                  <input
                    type="checkbox"
                    checked={scopeChecked}
                    onChange={(e) => handleSetCompromiseScope(e.target.checked ? SCOPE_NO_ACCESS : SCOPE_ACCESS_CONFIRMED)}
                    disabled={scopeSaving || !scopeAllowed}
                    className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 h-3.5 w-3.5 disabled:opacity-50"
                  />
                  No attacker access found
                </label>
              );
            })()}
            <label className={`flex items-center gap-1 text-xs ${(!attackerWindow || !results) ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-pointer'}`}>
              <input
                type="checkbox"
                checked={includeLogBreakdown}
                onChange={(e) => setIncludeLogBreakdown(e.target.checked)}
                disabled={!attackerWindow || !results}
                className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 h-3.5 w-3.5"
              />
              Log breakdown
            </label>
            <div className="relative group">
              <button
                onClick={handleClientReport}
                disabled={!attackerWindow || !results}
                className="btn-secondary py-1.5 text-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <DocumentTextIcon className="h-4 w-4" />
                Client Report
              </button>
              {(!attackerWindow || !results) && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-xs text-gray-300 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                  Classify at least one IP as Malicious to enable reports (a successful phishing-kit sign-in also anchors the window)
                </div>
              )}
            </div>
            <div className="relative group">
              {/* Same gate as the deterministic reports (a report needs at least one
                  malicious IP to have anything to say), plus a demo-mode disable —
                  generation costs a real provider call and writes a durable artifact,
                  so it has no place in a prospect demo. */}
              <button
                onClick={() => setExecReportType('executive-brief')}
                disabled={!attackerWindow || !results || isDemoMode()}
                title={isDemoMode() ? DEMO_UNAVAILABLE_TITLE : undefined}
                className="btn-secondary py-1.5 text-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                Executive Brief
              </button>
              {(!attackerWindow || !results) && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-xs text-gray-300 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                  Classify at least one IP as Malicious to enable reports (a successful phishing-kit sign-in also anchors the window)
                </div>
              )}
            </div>
            <div className="relative group">
              {/* Same gate, same demo-mode disable, same tooltip as the Brief beside it --
                  both spend a real provider call and write a durable artifact, and only one
                  can run per case at a time. */}
              <button
                onClick={() => setExecReportType('executive-report')}
                disabled={WITHHELD_REPORTS.executiveReport || !attackerWindow || !results || isDemoMode()}
                title={WITHHELD_REPORTS.executiveReport ? WITHHELD_TITLE : (isDemoMode() ? DEMO_UNAVAILABLE_TITLE : undefined)}
                className="btn-secondary py-1.5 text-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <SparklesIcon className="h-4 w-4" />
                Executive Report
              </button>
              {/* The withheld reason takes precedence over the malicious-IP hint: the
                  button will not run even once an IP is classified, so showing the hint
                  would describe a gate that is no longer the operative one. */}
              {(WITHHELD_REPORTS.executiveReport || !attackerWindow || !results) && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-xs text-gray-300 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                  {WITHHELD_REPORTS.executiveReport ? WITHHELD_TITLE : 'Classify at least one IP as Malicious to enable reports (a successful phishing-kit sign-in also anchors the window)'}
                </div>
              )}
            </div>
            <div className="relative group">
              <button
                onClick={handleTechnicalReport}
                disabled={WITHHELD_REPORTS.technicalReport || !attackerWindow || !results}
                title={WITHHELD_REPORTS.technicalReport ? WITHHELD_TITLE : undefined}
                className="btn-secondary py-1.5 text-sm flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ClipboardDocumentListIcon className="h-4 w-4" />
                Technical Report
              </button>
              {(WITHHELD_REPORTS.technicalReport || !attackerWindow || !results) && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-xs text-gray-300 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                  {WITHHELD_REPORTS.technicalReport ? WITHHELD_TITLE : 'Classify at least one IP as Malicious to enable reports (a successful phishing-kit sign-in also anchors the window)'}
                </div>
              )}
            </div>
            <button
              onClick={handleToggleStatus}
              disabled={statusUpdating || runbookRunning || deleting}
              className={`py-1.5 text-sm ${caseData.status === 'Closed' ? 'btn-primary' : 'btn-secondary'}`}
            >
              {statusUpdating ? (
                <span className="flex items-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Updating...
                </span>
              ) : caseData.status === 'Closed' ? (
                'Reopen Case'
              ) : (
                'Close Case'
              )}
            </button>
            {isAdmin && (
              <button
                onClick={handleDeleteCase}
                disabled={deleting || runbookRunning || statusUpdating}
                className="btn-danger flex items-center gap-2 whitespace-nowrap py-1.5 text-sm"
              >
                {deleting ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Status update error */}
        {statusError && (
          <div className="mt-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-300">Failed to update case status</p>
              <p className="text-sm text-red-400 mt-1">{statusError}</p>
            </div>
            <button
              onClick={() => setStatusError(null)}
              className="text-red-400 hover:text-red-300 flex-shrink-0"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Case comment — post-investigation analyst remark (also shown in the Cases list) */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Comment
          </label>
          <textarea
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            onBlur={handleSaveComment}
            rows={2}
            placeholder="Add a closing comment for this investigation… (saved when you click away)"
            className="input-field text-sm w-full resize-y"
          />
          <div className="mt-1 h-4 text-xs text-gray-500">
            {commentSaving
              ? 'Saving…'
              : (commentDraft !== comment ? 'Unsaved — click away to save' : '')}
          </div>
        </div>

        {/* Runbook Status Indicator */}
        {runbookRunning && runbookStatus && (
          <div className="mt-4 p-3 bg-gray-800 rounded-lg border border-gray-700">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin h-5 w-5 text-blue-400 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-200">
                  {runbookStatus.message || 'Running investigation...'}
                </p>
              </div>
              <span className="text-xs text-gray-500 flex-shrink-0">
                {runbookStatus.status || ''}
              </span>
            </div>
          </div>
        )}

        {/* Runbook Completion */}
        {!runbookRunning && runbookStatus && runbookStatus.status === 'Completed' && (
          <div className="mt-4 p-3 bg-green-900/30 border border-green-700/50 rounded-lg flex items-center gap-3">
            <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
            <p className="text-sm text-green-300">
              Investigation completed successfully.
            </p>
            <button
              onClick={() => setRunbookStatus(null)}
              className="ml-auto text-green-400 hover:text-green-300"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Runbook Error. Two different problems share this banner, and calling both
            of them "Investigation failed" is how a completed run got reported as a
            failure (v1.146.1): a status read that died is amber and says so, while a
            genuine terminal state from Automation stays red. */}
        {runbookError && (
          <div className={`mt-4 p-3 rounded-lg flex items-start gap-3 border ${
            runbookOutcomeUnknown
              ? 'bg-amber-900/30 border-amber-600/60'
              : 'bg-red-900/30 border-red-700/50'
          }`}>
            <ExclamationTriangleIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
              runbookOutcomeUnknown ? 'text-amber-400' : 'text-red-400'
            }`} />
            <div>
              <p className={`text-sm font-medium ${runbookOutcomeUnknown ? 'text-amber-200' : 'text-red-300'}`}>
                {runbookOutcomeUnknown ? "Couldn't confirm the investigation's status" : 'Investigation failed'}
              </p>
              <p className={`text-sm mt-1 ${runbookOutcomeUnknown ? 'text-amber-400/90' : 'text-red-400'}`}>{runbookError}</p>
            </div>
            <button
              onClick={() => { setRunbookError(null); setRunbookOutcomeUnknown(false); }}
              className={`ml-auto ${runbookOutcomeUnknown ? 'text-amber-400 hover:text-amber-300' : 'text-red-400 hover:text-red-300'}`}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Identity scope. Kept separate from the data-source banner below: nothing
          failed to load here, the search was simply narrower than it could have been.
          A person's mailbox often logs under a different address than their sign-ins,
          so an unresolved identity means an empty tab might be a lookup gap rather
          than an absence of activity. Not dismissible, for the same reason. */}
      {identityBanner && (
        <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 border ${
          identityBanner.tone === 'warning'
            ? 'bg-amber-900/30 border-amber-600/60'
            : 'bg-sky-900/20 border-sky-700/50'
        }`}>
          {identityBanner.tone === 'warning'
            ? <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            : <InformationCircleIcon className="h-5 w-5 text-sky-400 flex-shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${identityBanner.tone === 'warning' ? 'text-amber-200' : 'text-sky-200'}`}>
              {identityBanner.title}
            </p>
            <p className={`text-xs mt-1 ${identityBanner.tone === 'warning' ? 'text-amber-400/90' : 'text-sky-400/90'}`}>
              {identityBanner.body}
            </p>
            {identityBanner.notes && (
              <p className={`text-xs mt-1 ${identityBanner.tone === 'warning' ? 'text-amber-300/70' : 'text-sky-300/70'}`}>
                {identityBanner.notes}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Data-source failures. A workload query that fails is coerced to an empty
          result set by the runbook, which renders as a clean "0 events" — visually
          identical to "nothing happened". Deliberately NOT dismissible: reading a
          failed query as an absence of activity is the exact error this prevents. */}
      {results?.DataSourceErrors?.length > 0 && (
        <div className="mb-6 p-4 bg-amber-900/30 border border-amber-600/60 rounded-lg flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-200">
              Incomplete results — {results.DataSourceErrors.length} data source
              {results.DataSourceErrors.length > 1 ? 's' : ''} failed to load
            </p>
            <p className="text-xs text-amber-400/90 mt-1">
              Data is missing for the workloads below. A zero count for these does <span className="font-semibold">not</span> mean
              no events occurred. Re-run the investigation — a shorter date range is more likely to succeed.
            </p>
            <ul className="mt-2 space-y-1">
              {results.DataSourceErrors.map((e, i) => (
                <li key={`${e.workload}-${i}`} className="text-xs text-amber-300 flex items-start gap-1.5">
                  <span className="text-amber-500 mt-px">&bull;</span>
                  <span>
                    <span className="font-medium">{e.label}</span>
                    {e.message ? ` — ${e.message}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-800 mb-6">
        <div className="flex items-center justify-between">
          <nav className="flex gap-1" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-700'
                }`}
              >
                {tab.label}
                {tab.id === 'service-ips' && filteredResults.serviceExcluded && filteredResults.serviceExcluded.totalExcluded > 0 && (
                  <span className="bg-gray-700 text-gray-400 text-xs rounded-full px-1.5 py-0.5">
                    {filteredResults.serviceExcluded.totalExcluded}
                  </span>
                )}
                {tab.id === 'safe-ips' && filteredResults.safeExcluded && filteredResults.safeExcluded.totalExcluded > 0 && (
                  <span className="bg-blue-900/60 text-blue-300 text-xs rounded-full px-1.5 py-0.5">
                    {filteredResults.safeExcluded.totalExcluded}
                  </span>
                )}
              </button>
            ))}
          </nav>
          {/* Every pill is ALWAYS rendered — see ui/FilterPill.jsx for why. A zero count
              shows as (0) and the pill dims; a pill that cannot act at all renders
              disabled with the reason. */}
          <div className="flex flex-wrap items-center gap-2 pb-1">
            {/* Same action as "Set the report view" in the report dialog. Disabled, with
                the reason, once every pill already matches -- never a live no-op. */}
            <button
              type="button"
              data-testid="set-report-view"
              onClick={applyReportFilterView}
              disabled={reportViewDiffCount === 0}
              className="btn-secondary flex items-center gap-1.5 py-1 text-xs disabled:opacity-50 disabled:cursor-default"
              title={reportViewDiffCount === 0
                ? 'The filters already match the report view.'
                : `Set every filter to the report view (${reportViewDiffCount} differ${reportViewDiffCount === 1 ? 's' : ''}). This changes what the case shows; you can set any filter back afterwards.`}
            >
              <FunnelIcon className="h-3.5 w-3.5" />
              {reportViewDiffCount === 0 ? 'Report view set' : 'Set the report view'}
            </button>
            <FilterPill
              icon={ShieldCheckIcon}
              engaged={hideSafeIPs}
              inert={effectiveSafeIPs.length === 0 && safeMarkIds.size === 0}
              label={`${hideSafeIPs ? 'Safe IPs hidden' : 'Safe IPs shown'} (${effectiveSafeIPs.length}${safeMarkIds.size ? ` + ${safeMarkIds.size} row${safeMarkIds.size === 1 ? '' : 's'}` : ''})`}
              title={effectiveSafeIPs.length === 0 && safeMarkIds.size === 0
                ? 'No IP or row on this case is marked safe yet, so this filter has nothing to hide.'
                : `${hideSafeIPs ? 'Show' : 'Hide'} events from safe IPs${safeMarkIds.size ? ' and the rows marked Safe' : ''}`}
              onClick={() => setHideSafeIPs(!hideSafeIPs)}
            />
            <FilterPill
              icon={CheckCircleIcon}
              engaged={hideKnownApps}
              inert={knownApps.length === 0}
              label={hideKnownApps ? `Safe apps hidden (${knownApps.length})` : `Safe apps shown (${knownApps.length})`}
              title={knownApps.length === 0
                ? 'No application is flagged as known/safe for this tenant, so this filter has nothing to hide.'
                : (hideKnownApps ? 'Show safe app events' : 'Hide safe app events')}
              onClick={() => setHideKnownApps(!hideKnownApps)}
            />
            <FilterPill
              icon={CpuChipIcon}
              engaged={hideMsSubstrate}
              label={hideMsSubstrate ? 'MS substrate hidden' : 'MS substrate shown'}
              title={hideMsSubstrate
                ? 'Show Microsoft substrate (background) reads'
                : 'Hide Microsoft substrate (background) reads — sessionless, self-issued-token reads with no user or attacker activity'}
              onClick={() => setHideMsSubstrate(!hideMsSubstrate)}
            />
            <FilterPill
              icon={InboxStackIcon}
              engaged={hideOutlookNoise}
              inert={!hasOutlookNoise}
              label={hideOutlookNoise ? 'Outlook noise hidden' : 'Outlook noise shown'}
              title={!hasOutlookNoise
                ? 'This case has no Outlook background telemetry (OutlookItemPrioritized), so this filter has nothing to hide.'
                : (hideOutlookNoise
                  ? 'Show Outlook background events (OutlookItemPrioritized)'
                  : 'Hide noisy Outlook background events (OutlookItemPrioritized) — mailbox priority-classification telemetry with no user or attacker activity')}
              onClick={() => setHideOutlookNoise(!hideOutlookNoise)}
            />
            {/* The one SEMANTIC guard: with an indeterminate window "post-window" has no
                meaning, so the pill is disabled with a reason rather than live and inert. */}
            <FilterPill
              icon={ClockIcon}
              tone="purple"
              engaged={hidePostWindow}
              inert={filteredResults.postWindowCount === 0}
              disabled={!attackerWindow || attackerWindow.isIndeterminate}
              label={hidePostWindow
                ? `Post-window hidden (${filteredResults.postWindowCount || 0})`
                : `Post-window events (${filteredResults.postWindowCount || 0})`}
              title={(!attackerWindow || attackerWindow.isIndeterminate)
                ? 'The end of the attacker window could not be determined, so there is no "after the window" to hide. Classify a malicious IP, or record containment, to establish a cutoff.'
                : (hidePostWindow ? 'Show post-window events' : 'Hide events after attacker window closes')}
              onClick={() => setHidePostWindow(!hidePostWindow)}
            />
            {/* The other end of the window. Same semantic guard as Post-window: with an
                indeterminate window there is no "before" either, so it is disabled with a
                reason rather than live and inert.

                🔴 The title says what engaging it MEANS, not just what it does. This is the
                one pill whose engaged state asserts a judgment about evidence the analyst
                may not have examined -- activity before the window is where an earlier
                foothold would be -- so the copy has to invite the check rather than present
                the rows as noise. */}
            <FilterPill
              icon={ClockIcon}
              tone="purple"
              engaged={hidePreWindow}
              inert={filteredResults.preWindowCount === 0}
              disabled={!attackerWindow || attackerWindow.isIndeterminate}
              label={hidePreWindow
                ? `Before window hidden (${filteredResults.preWindowCount || 0})`
                : `Before window (${filteredResults.preWindowCount || 0})`}
              title={(!attackerWindow || attackerWindow.isIndeterminate)
                ? 'The attacker window has no established start, so there is no "before the window" to hide. Classify a malicious IP to establish one.'
                : (hidePreWindow
                  ? 'Show activity from before the attacker window opened again.'
                  : (filteredResults.preWindowCount === 0
                    ? 'No activity in the current view predates the attacker window, so this filter has nothing to hide.'
                    : 'Hide activity from before the attacker window opened (events from IPs not classified Malicious). Check it first: activity before the window is where an EARLIER foothold would show, and hiding it says you looked and it is not part of the story. Events from a malicious IP are never hidden.'))}
              onClick={() => setHidePreWindow(!hidePreWindow)}
            />
            {/* Risk Detections. Hides every risk-detection row from All Events — the
                Risk Detections sub-tab reads the unfiltered set and is unaffected, which
                is what makes hiding all of them safe rather than lossy. */}
            <FilterPill
              icon={ShieldExclamationIcon}
              tone="purple"
              engaged={hideRiskDetections}
              inert={(filteredResults.riskDetectionAllEventsCount || 0) === 0}
              // The count is taken BEFORE the rows are hidden, so it is the same number in
              // both states — and like every other pill, the engaged label keeps it.
              label={hideRiskDetections
                ? `Risk detections hidden (${filteredResults.riskDetectionAllEventsCount || 0})`
                : `Risk detections (${filteredResults.riskDetectionAllEventsCount || 0})`}
              title={hideRiskDetections
                ? 'Show Identity Protection risk-detection rows in All Events again.'
                : ((filteredResults.riskDetectionAllEventsCount || 0) === 0
                  ? 'This case has no Identity Protection risk-detection rows in the current view, so there is nothing to hide.'
                  : 'Hide Identity Protection risk-detection rows from All Events. Microsoft writes one of these about a sign-in, so most duplicate a sign-in row at the same second and state no risk level of their own. They remain in full on the Risk Detections tab.')}
              onClick={() => setHideRiskDetections(!hideRiskDetections)}
            />
            <FilterPill
              icon={KeyIcon}
              engaged={hidePreCompromise}
              inert={filteredResults.preCompromiseCount === 0}
              label={hidePreCompromise
                ? `Pre-compromise hidden (${filteredResults.preCompromiseCount || 0})`
                : `Pre-compromise events (${filteredResults.preCompromiseCount || 0})`}
              title={filteredResults.preCompromiseCount === 0
                ? 'No compromised session has activity before its first malicious IP, so this filter has nothing to hide. It only takes effect once an IP is classified Malicious.'
                : (hidePreCompromise
                  ? 'Show pre-compromise session activity (legitimate prior use of compromised sessions)'
                  : 'Hide pre-compromise session activity (events on compromised sessions before the malicious IP first appeared)')}
              onClick={() => setHidePreCompromise(!hidePreCompromise)}
            />
            <FilterPill
              icon={ShieldCheckIcon}
              engaged={hideSafeSessions}
              inert={filteredResults.safeSessionCount === 0}
              label={hideSafeSessions
                ? `Safe sessions hidden (${filteredResults.safeSessionCount || 0})`
                : `Safe sessions (${filteredResults.safeSessionCount || 0})`}
              title={filteredResults.safeSessionCount === 0
                ? 'No session on this case is marked safe, so this filter has nothing to hide.'
                : (hideSafeSessions ? 'Show events from safe sessions' : 'Hide events from sessions classified as safe by the analyst')}
              onClick={() => setHideSafeSessions(!hideSafeSessions)}
            />
            <FilterPill
              icon={LinkIcon}
              engaged={hideSafeIpSessions}
              inert={filteredResults.safeIpSessionCount === 0}
              label={hideSafeIpSessions
                ? `Session-safe extended (${filteredResults.safeIpSessionCount || 0})`
                : `Extend safe to sessions (${filteredResults.safeIpSessionCount || 0})`}
              title={filteredResults.safeIpSessionCount === 0
                ? 'No session was first seen on a Safe IP, so this filter has nothing to hide.'
                : (hideSafeIpSessions
                  ? 'Showing non-sign-in events from sessions first seen on a Safe IP'
                  : 'Hide non-sign-in events from sessions first seen on a Safe IP. Sessions that also touched a malicious IP, and all sign-ins, are always kept.')}
              onClick={() => setHideSafeIpSessions(!hideSafeIpSessions)}
            />
            <FilterPill
              icon={XMarkIcon}
              engaged={hideFailedEvents}
              inert={filteredResults.failedEventCount === 0}
              label={hideFailedEvents
                ? `Failed hidden (${filteredResults.failedEventCount || 0})`
                : `Failed events (${filteredResults.failedEventCount || 0})`}
              title={filteredResults.failedEventCount === 0
                ? 'This case has no failed events, so this filter has nothing to hide.'
                : (hideFailedEvents ? 'Show failed events' : 'Hide failed events from timeline')}
              onClick={() => setHideFailedEvents(!hideFailedEvents)}
            />
            <FilterPill
              icon={ShieldCheckIcon}
              engaged={hideDlpEvents}
              inert={filteredResults.dlpEventCount === 0}
              label={hideDlpEvents
                ? `DLP hidden (${filteredResults.dlpEventCount || 0})`
                : `DLP events (${filteredResults.dlpEventCount || 0})`}
              title={filteredResults.dlpEventCount === 0
                ? 'This case has no DLP policy matches, so this filter has nothing to hide.'
                : (hideDlpEvents ? 'Show DLP policy match events' : 'Hide DLP policy match events from timeline')}
              onClick={() => setHideDlpEvents(!hideDlpEvents)}
            />
            <FilterPill
              icon={InboxStackIcon}
              tone="purple"
              engaged={hideUnresolvedMailAccess}
              inert={filteredResults.unresolvedMailAccessCount === 0}
              label={hideUnresolvedMailAccess
                ? `Unresolved mail/attachment reads hidden (${filteredResults.unresolvedMailAccessCount || 0})`
                : `Unresolved mail/attachment reads (${filteredResults.unresolvedMailAccessCount || 0})`}
              title={filteredResults.unresolvedMailAccessCount === 0
                ? 'Every mail/attachment read on this case resolved past the Microsoft service proxy, so this filter has nothing to hide.'
                : (hideUnresolvedMailAccess
                  ? 'Show MailItemsAccessed / AttachmentAccess events with unresolved service IPs'
                  : 'Hide MailItemsAccessed and AttachmentAccess events whose IP could not be resolved past the Microsoft service proxy (Unresolved-ServiceIP) — high-volume noise')}
              onClick={() => setHideUnresolvedMailAccess(!hideUnresolvedMailAccess)}
            />
            <FilterPill
              icon={ArrowPathIcon}
              engaged={hideLookback}
              inert={(filteredResults.totalCorrelationCount || 0) === 0}
              label={hideLookback
                ? `Lookback hidden (${filteredResults.lookbackEventCount || 0})`
                : `Lookback shown (${filteredResults.totalCorrelationCount || 0})`}
              title={(filteredResults.totalCorrelationCount || 0) === 0
                ? 'No lookback correlation sign-ins were pulled for this case, so this filter has nothing to hide.'
                : (hideLookback ? 'Show lookback sign-in events (pre-investigation context)' : 'Hide lookback sign-in events from timeline')}
              onClick={() => setHideLookback(!hideLookback)}
            />
            <FilterPill
              icon={EnvelopeIcon}
              engaged={hideEmailIPs}
              inert={emailOnlyIPCount === 0}
              label={hideEmailIPs ? `Email-only IPs marked (${emailOnlyIPCount})` : `Email-only IPs (${emailOnlyIPCount})`}
              title={emailOnlyIPCount === 0
                ? 'No IP on this case appears only in message-trace events, so there is nothing to mark.'
                : (hideEmailIPs
                  ? 'Stop badging IPs that appear only in message-trace events'
                  : 'Badge IPs that appear only in message-trace events. These are no longer hidden from the IP Addresses table (v1.147.1) — that table is where IPs get classified, so removing a row there removed the ability to act on it.')}
              onClick={() => setHideEmailIPs(!hideEmailIPs)}
            />
            {/* Three-state, so it rides the same pill with a caller-computed tone. */}
            <FilterPill
              icon={EyeSlashIcon}
              tone={dismissedMode === 'only' ? 'orange' : 'blue'}
              engaged={dismissedMode !== 'shown'}
              inert={filteredResults.dismissedEventCount === 0}
              label={dismissedMode === 'hidden'
                ? `Dismissed hidden (${filteredResults.dismissedEventCount || 0})`
                : dismissedMode === 'only'
                  ? `Dismissed only (${filteredResults.dismissedEventCount || 0})`
                  : `Dismissed shown (${filteredResults.dismissedEventCount || 0})`}
              title={filteredResults.dismissedEventCount === 0
                ? 'No event on this case has been dismissed, so this filter has nothing to hide.'
                : (dismissedMode === 'hidden' ? 'Show dismissed events' : dismissedMode === 'shown' ? 'Show only dismissed events' : 'Hide dismissed events')}
              onClick={() => setDismissedMode((prev) => prev === 'hidden' ? 'shown' : prev === 'shown' ? 'only' : 'hidden')}
            />
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {/* Loading state for results */}
        {resultsLoading && (
          <div className="card flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <svg
                className="animate-spin h-8 w-8 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-gray-500 text-sm">Loading results...</span>
            </div>
          </div>
        )}

        {/* Error state for results */}
        {resultsError && (
          <div className="card text-center py-12">
            <ExclamationTriangleIcon className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <p className="text-gray-400 mb-3">{resultsError}</p>
            <button onClick={loadResults} className="btn-secondary text-sm">
              Retry
            </button>
          </div>
        )}

        {/* No results state */}
        {!resultsLoading && !resultsError && !results && (
          <div className="card text-center py-16">
            <PlayIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No Results Yet
            </h3>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              Run an investigation to analyze sign-in events, IP addresses, and
              generate a security summary for this case.
            </p>
          </div>
        )}

        {/* Results */}
        {!resultsLoading && !resultsError && results && (
          <SessionMarkContext.Provider value={sessionMarkCtx}>
            {/* By-design scope gap, stated on every tab: the runbook reads only
                silver_m365_unified_audit_events, and the non-interactive sign-in
                feed is a separate Delta table. Not dismissible — see the
                component for the measured harm. */}
            <CaseScopeNotice collected={Boolean(results?.NonInteractiveCollected)} />
            {activeTab === 'timeline' && caseAlertsError && (
              <div className="mb-3 rounded border border-amber-700/50 bg-amber-900/20 px-3 py-2 text-xs text-amber-200">
                Blue 365 Signal alerts for this user could not be loaded, so none are shown in All Events.
                This does not mean there are none. ({caseAlertsError})
              </div>
            )}
            {activeTab === 'timeline' && (
              <TimelineView
                legAttribution={legAttribution}
                meetingContext={meetingContext}
                signInEvents={filteredResults.filtered?.SignInEvents || []}
                exchangeEvents={filteredResults.filtered?.ExchangeEvents || []}
                directoryAuditEvents={filteredResults.filtered?.DirectoryAuditEvents || []}
                sharePointEvents={filteredResults.filtered?.SharePointEvents || []}
                messageTraceEvents={filteredResults.filtered?.MessageTraceEvents || []}
                urlClickEvents={filteredResults.filtered?.UrlClickEvents || []}
                rawUrlClickEvents={results?.UrlClickEvents || []}
                rawDirectoryAuditEvents={results?.DirectoryAuditEvents || []}
                dlpEvents={filteredResults.filtered?.DlpEvents || []}
                generalEvents={filteredResults.filtered?.GeneralEvents || []}
                azureActivityEvents={filteredResults.filtered?.AzureActivityEvents || []}
                riskDetectionEvents={filteredResults.filtered?.RiskDetectionEvents || []}
                riskyUserEvents={filteredResults.filtered?.RiskyUserEvents || []}
                securityAlertEvents={filteredResults.filtered?.SecurityAlertEvents || []}
                securityIncidentEvents={filteredResults.filtered?.SecurityIncidentEvents || []}
                nonInteractiveSignInEvents={filteredResults.filtered?.NonInteractiveSignInEvents || []}
                caseAlertEvents={filteredResults.filtered?.CaseAlertEvents || []}
                ipData={filteredResults.filtered?.IPAddresses || []}
                apps={results?.enterpriseApps || []}
                knownApps={knownApps}
                tenantId={caseData?.tenantId}
                onToggleKnownApp={handleToggleKnownApp}
                knownAppSaving={knownAppSaving}
                isAdmin={isAdmin}
                hideKnownApps={hideKnownApps}
                hideMsSubstrate={hideMsSubstrate}
                hideOutlookNoise={hideOutlookNoise}
                safeIPSet={effectiveSafeIPSet}
                ipClassifications={ipClassifications}
                onSetIPStatus={handleSetIPStatus}
                ipComments={ipComments}
                identityResolution={results?.IdentityResolution || null}
                subTabEvents={baseResults}
                summary={filteredResults.filtered?.Summary || {}}
                caseData={{
                  ...caseData,
                  resultsStartDate: results?.Parameters?.startDate || '',
                  resultsEndDate: results?.Parameters?.endDate || '',
                }}
                selectedTz={selectedTz}
                revealSubjects={revealSubjects}
                onToggleRevealSubjects={handleToggleRevealSubjects}
                attackerWindow={attackerWindow}
                compromiseScope={compromiseScope}
                hidePostWindow={hidePostWindow}
                onTogglePostWindow={() => setHidePostWindow(h => !h)}
                postWindowFilteredCount={filteredResults.postWindowCount || 0}
                compromisedSessions={compromisedSessions}
                hidePreCompromise={hidePreCompromise}
                onTogglePreCompromise={() => setHidePreCompromise(h => !h)}
                preCompromiseCount={filteredResults.preCompromiseCount || 0}
                sessionClassifications={sessionClassifications}
                safeSessionSet={safeSessionSet}
                onCycleSessionStatus={handleCycleSessionStatus}
                otherSessionsSummary={otherSessionsSummary}
                dismissedEventIds={dismissedEventIds}
                onDismissEvent={handleDismissEvent}
                attackerEventMarks={attackerEventMarks}
                onSetAttackerMark={handleSetAttackerMark}
                urlClassifications={urlClassifications}
                onCycleUrlStatus={handleCycleUrlStatus}
                phishingFlags={phishingFlags}
                onTogglePhishingFlag={handleTogglePhishingFlag}
                relatedPhishingMessages={results?.RelatedPhishingMessages || []}
                userBaseline={results?.UserBaseline || null}
              />
            )}
            {activeTab === 'ips' && (
              <IPAddressTable
                ipData={filteredResults.filtered?.IPAddresses || []}
                ipClassifications={ipClassifications}
                ipComments={ipComments}
                onUpdateIPComment={handleSetIPComment}
                tenantSafeIPs={tenantSafeIPs}
                onCycleIPStatus={handleCycleIPStatus}
                isSaving={ipClassSaving}
                signInEvents={results?.SignInEvents || []}
                userBaseline={results?.UserBaseline || null}
                serviceIPLabelMap={serviceIPLabelMap}
                isAdmin={isAdmin}
                onRefreshIpEnrichment={handleRefreshIpEnrichment}
                onRefreshIpEnrichmentBatch={handleRefreshIpEnrichmentBatch}
                onFetchRealUserEvidence={handleFetchRealUserEvidence}
                onSetIPSafe={handleSetIPSafe}
                onSetIPsSafe={handleSetIPsSafe}
                initialEvidence={persistedEvidence}
                onEvidenceChange={handleEvidenceChange}
                caseId={caseData.caseId}
                tenantId={caseData.tenantId}
                onAddMaliciousToKnownList={handleAddMaliciousIPsToKnownList}
                markEmailOnlyIPs={hideEmailIPs}
                onFetchNonInteractive={handleFetchNonInteractive}
                nonInteractive={nonInteractive}
                sessionIndex={caseSessionIndex}
                hasMaliciousClassification={Object.values(ipClassifications || {}).some((v) => v === 'malicious')}
                activityInWindow={activityInWindowFn}
                caseStartDate={(caseData.startDate || '').substring(0, 10)}
                iocDate={(caseData.iocDate || '').substring(0, 10)}
                firstAttackerActivity={attackerWindow?.firstAttackerActivity || ''}
                caseDeviceNames={caseDeviceNames}
              />
            )}
            {activeTab === 'devices' && (
              <DevicesTab
                signInEvents={results?.SignInEvents || []}
                ipClassifications={ipClassifications}
                ipComments={ipComments}
                selectedTz={selectedTz}
                onCycleIPStatus={handleCycleIPStatus}
                userBaseline={results?.UserBaseline || null}
                nonInteractive={nonInteractive}
                caseStartDate={(caseData.startDate || '').substring(0, 10)}
                caseEndDate={(caseData.endDate || '').substring(0, 10)}
              />
            )}
            {activeTab === 'sessions' && (
              /* Deliberately fed the UNFILTERED results. The point of this tab
                 is which IPs share a session id, so hiding safe/service IPs
                 would remove exactly the rows that make a mixed session
                 visible. Every other tab honours the filter bar; this one
                 answers a question the filters would destroy. */
              <SessionsTab
                results={results}
                /* Unfiltered IP rows purely for provider / network-type / geo
                   labels beside each IP. */
                ipData={results?.IPAddresses || []}
                /* Drives the "User?" column — the user's own pre-incident
                   sign-in IPs, from the 7-day behaviour baseline. */
                userBaseline={results?.UserBaseline || null}
                ipClassifications={ipClassifications}
                /* tenantSafeIPs, NOT effectiveSafeIPSet — must match the rule
                   IPAddressTable colours with, or one IP shows two verdicts on
                   two tabs. See the comment on SessionsTab.ipStatusOf. */
                tenantSafeIPs={tenantSafeIPs}
                compromisedSessions={compromisedSessions}
                sessionClassifications={sessionClassifications}
                onCycleSessionStatus={handleCycleSessionStatus}
                selectedTz={selectedTz}
              />
            )}
            {activeTab === 'baseline' && (
              <BaselineTab
                caseId={caseData.caseId}
                iocDate={(caseData.iocDate || '').substring(0, 10)}
              />
            )}
            {activeTab === 'summary' && (
              <SummaryView summary={filteredResults.filtered?.Summary || {}} />
            )}
            {activeTab === 'raw' && (
              <RawLogViewer events={[
                ...(filteredResults.filtered?.SignInEvents || []).map(e => ({ ...e, _type: 'SignIn' })),
                ...(filteredResults.filtered?.ExchangeEvents || []).map(e => ({ ...e, _type: 'Exchange' })),
                ...(filteredResults.filtered?.DirectoryAuditEvents || []).map(e => ({ ...e, _type: 'DirectoryAudit' })),
                ...(filteredResults.filtered?.SharePointEvents || []).map(e => ({ ...e, _type: 'SharePoint' })),
                ...(filteredResults.filtered?.MessageTraceEvents || []).map(e => ({ ...e, _type: 'MessageTrace' })),
                ...(filteredResults.filtered?.UrlClickEvents || []).map(e => ({ ...e, _type: 'UrlClick' })),
                ...(filteredResults.filtered?.DlpEvents || []).map(e => ({ ...e, _type: 'DLP' })),
                ...(filteredResults.filtered?.GeneralEvents || []).map(e => ({ ...e, _type: 'General' })),
                ...(filteredResults.filtered?.AzureActivityEvents || []).map(e => ({ ...e, _type: 'AzureActivity' })),
                ...(filteredResults.filtered?.RiskDetectionEvents || []).map(e => ({ ...e, _type: 'RiskDetection' })),
                ...(filteredResults.filtered?.RiskyUserEvents || []).map(e => ({ ...e, _type: 'RiskyUser' })),
                ...(filteredResults.filtered?.SecureScoreEvents || []).map(e => ({ ...e, _type: 'SecureScore' })),
                ...(filteredResults.filtered?.SecurityAlertEvents || []).map(e => ({ ...e, _type: 'SecurityAlert' })),
                ...(filteredResults.filtered?.SecurityIncidentEvents || []).map(e => ({ ...e, _type: 'SecurityIncident' })),
              ].sort((a, b) => (b.EventTimeUTC || '').localeCompare(a.EventTimeUTC || ''))} revealSubjects={revealSubjects} />
            )}
            {activeTab === 'service-ips' && (
              <ServiceIPEventsTab
                excludedSignIn={filteredResults.serviceExcluded?.signInEvents || []}
                excludedExchange={filteredResults.serviceExcluded?.exchangeEvents || []}
                excludedSharePoint={filteredResults.serviceExcluded?.sharePointEvents || []}
                excludedIPs={filteredResults.serviceExcluded?.ipAddresses || []}
                totalExcluded={filteredResults.serviceExcluded?.totalExcluded || 0}
                totalEvents={(results?.SignInEvents || []).length + (results?.ExchangeEvents || []).length + (results?.SharePointEvents || []).length}
                serviceIPCount={serviceIPs.length}
                serviceIPs={serviceIPs}
                onServiceIPsChanged={setServiceIPs}
              />
            )}
            {activeTab === 'safe-ips' && (
              <SafeIPEventsTab
                legAttribution={legAttribution}
                excludedSignIn={filteredResults.safeExcluded?.signInEvents || []}
                excludedExchange={filteredResults.safeExcluded?.exchangeEvents || []}
                excludedDirectory={filteredResults.safeExcluded?.directoryAuditEvents || []}
                excludedSharePoint={filteredResults.safeExcluded?.sharePointEvents || []}
                excludedMessageTrace={filteredResults.safeExcluded?.messageTraceEvents || []}
                excludedUrlClicks={filteredResults.safeExcluded?.urlClickEvents || []}
                excludedDlp={filteredResults.safeExcluded?.dlpEvents || []}
                excludedGeneral={filteredResults.safeExcluded?.generalEvents || []}
                excludedAzureActivity={filteredResults.safeExcluded?.azureActivityEvents || []}
                excludedRiskDetection={filteredResults.safeExcluded?.riskDetectionEvents || []}
                excludedSecurityAlert={filteredResults.safeExcluded?.securityAlertEvents || []}
                excludedIPs={filteredResults.safeExcluded?.ipAddresses || []}
                totalExcluded={filteredResults.safeExcluded?.totalExcluded || 0}
                totalEvents={(results?.SignInEvents || []).length + (results?.ExchangeEvents || []).length +
                             (results?.DirectoryAuditEvents || []).length + (results?.SharePointEvents || []).length +
                             (results?.MessageTraceEvents || []).length + (results?.UrlClickEvents || []).length +
                             (results?.DlpEvents || []).length + (results?.GeneralEvents || []).length +
                             (results?.AzureActivityEvents || []).length + (results?.RiskDetectionEvents || []).length +
                             (results?.SecurityAlertEvents || []).length}
                safeIPCount={effectiveSafeIPs.length}
                hideSafeIPs={hideSafeIPs}
                onToggleHide={() => setHideSafeIPs(!hideSafeIPs)}
              />
            )}
          </SessionMarkContext.Provider>
        )}
      </div>

      <ExecutiveReportModal
        open={!!execReportType}
        reportType={execReportType || 'executive-report'}
        onClose={() => setExecReportType(null)}
        caseId={caseId}
        reportParams={execReportParams}
        revealSubjects={revealSubjects}
        filterState={caseFilterState}
        onApplyReportView={applyReportFilterView}
        onBeforeGenerate={flushCaseJudgments}
      />
    </div>
  );
}
